import React, { useState, useEffect, useRef, useCallback } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  Image as ImageIcon,
  Loader2,
  AlertCircle,
  Crop,
  Download,
  Instagram,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Trash2,
  Filter,
  Check,
  Copy,
  Share2,
  Lock,
  Heart,
  Pencil,
  Play,
  Search,
  Clock,
  MoreHorizontal,
  Sparkles,
  Info,
  Maximize,
  GalleryVerticalEnd,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { supabase } from "../lib/supabase";
import { GENERATE_AUTH_TOKEN } from "../config";
import SubscriptionLimitModal from "../components/SubscriptionLimitModal";
import CarruselConfirmModal from "../components/CarruselConfirmModal";
import LoadingAnimation from "../components/LoadingAnimation";
import { trackInteraction } from "../utils/trackInteraction";
import {
  useCropState,
  ImageCropCanvas,
  ImageCropControls,
} from "../components/ImageCropModal";
import { getCachedSignedUrl, setCachedSignedUrl } from "../utils/imageCache";
import { getCached, setCached, CACHE_KEYS } from "../utils/sessionCache";
import {
  buildDownloadFileName,
  slugifyVariantName,
} from "../utils/downloadFileName";
import InAppBrowserModal from "../components/InAppBrowserModal";
import { isInAppBrowser } from "../hooks/useDeviceDetection";
import { sileo } from "sileo";

interface GeneratedImage {
  id: number;
  file_name: string;
  created_at: string;
  variante_id: number;
  variante_nombre: string;
  formato?: string; // Format of the image (e.g., 'square', 'portrait', 'landscape')
  tipo?: string; // Type of content (e.g., 'video', 'video_pro' for videos)
  favorito?: boolean;
  signedUrl?: string;
  thumbnailUrl?: string;
  filePath?: string; // Add filePath to store the direct path for the image
  upscaled_path?: string | null; // Path in storage to the upscaled version (if exists)
  upscaledSignedUrl?: string; // Cached signed URL for the upscaled version
}

// Helper to detect if an item is a video based on tipo column
const isVideoItem = (item?: GeneratedImage | null): boolean => {
  if (!item?.tipo) return false;
  return item.tipo === "video" || item.tipo === "video_pro";
};

// Helper to format date in a smart way
const formatSmartDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();

  // Reset time to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dayBeforeYesterday = new Date(today);
  dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const timeStr = `${hours}:${minutes} hs`;

  // Today
  if (dateOnly.getTime() === today.getTime()) {
    return `Hoy ${timeStr}`;
  }

  // Yesterday
  if (dateOnly.getTime() === yesterday.getTime()) {
    return `Ayer ${timeStr}`;
  }

  // Day before yesterday
  if (dateOnly.getTime() === dayBeforeYesterday.getTime()) {
    return `Anteayer ${timeStr}`;
  }

  // Within the last week - show day name
  if (dateOnly >= oneWeekAgo) {
    const dayNames = [
      "Domingo",
      "Lunes",
      "Martes",
      "Miércoles",
      "Jueves",
      "Viernes",
      "Sábado",
    ];
    return `${dayNames[date.getDay()]} ${timeStr}`;
  }

  // Older - show day and month
  const monthNames = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  return `${date.getDate()} de ${monthNames[date.getMonth()]}`;
};

const HISTORY_STATE_KEY = "atlas-history-state";

interface HistoryPersistedState {
  selectedTimeframe: "today" | "last7days" | "last30days" | "last90days";
  selectedVariant: number | string | null;
  onlyFavorites: boolean;
  searchQuery: string;
  scrollTop: number;
  currentViewImageId: number | null;
  mobileView: "grid" | "detail";
}

function loadHistoryState(): HistoryPersistedState | null {
  try {
    const raw = sessionStorage.getItem(HISTORY_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearHistoryState() {
  try {
    sessionStorage.removeItem(HISTORY_STATE_KEY);
  } catch {}
}

function saveHistoryState(state: HistoryPersistedState) {
  try {
    sessionStorage.setItem(HISTORY_STATE_KEY, JSON.stringify(state));
  } catch {}
}

export const GeneratedImagesHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const expandImageIdFromState = (location.state as any)?.expandImageId as
    | string
    | undefined;
  // Pending restore info — populated by mount effect, consumed after images load
  const pendingRestore = useRef<HistoryPersistedState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // New state for loading more images
  const [error, setError] = useState<string | null>(null);
  const [allImages, setAllImages] = useState<GeneratedImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<GeneratedImage[]>([]);
  const [variants, setVariants] = useState<
    { id: number | string; nombre: string }[]
  >([]);
  const [selectedVariant, setSelectedVariant] = useState<
    number | string | null
  >(null);

  // Multimarca: lista de brands del company + brand seleccionada en el filtro.
  // El filtro solo se muestra si brands.length > 1 (paid + onboarding completo).
  type BrandRow = {
    id: string;
    name: string;
    logo_url: string | null;
    is_default: boolean;
  };
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const brandDropdownRef = useRef<HTMLDivElement | null>(null);
  const [mobileSelectedBrandId, setMobileSelectedBrandId] = useState<
    string | null
  >(null);

  // Descarga masiva: modo selección + ids seleccionados.
  // GeneratedImage.id está tipado como number pero en runtime es UUID string;
  // usamos union para que ambos accesos compilen.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set(),
  );
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkError, setBulkError] = useState<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  // Drag-to-select (mobile): trackea touch para seleccionar varias en fila
  type DragSelectState = {
    active: boolean; // true una vez que se detecta movimiento
    toggleTo: "select" | "deselect";
    visited: Set<string | number>;
    startX: number;
    startY: number;
    hasMoved: boolean;
  };
  const dragSelectRef = useRef<DragSelectState | null>(null);

  const isMobileViewport = () =>
    typeof window !== "undefined" && window.innerWidth < 640;
  const SELECTION_LIMIT_DESKTOP = 50;
  const SELECTION_LIMIT_MOBILE = 30;
  const selectionLimit = isMobileViewport()
    ? SELECTION_LIMIT_MOBILE
    : SELECTION_LIMIT_DESKTOP;

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setBulkError(null);
  };

  const toggleSelected = (id: string | number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= selectionLimit) {
          setBulkError(
            `Máximo ${selectionLimit} elementos${isMobileViewport() ? " en mobile" : ""}`,
          );
          setTimeout(() => setBulkError(null), 2500);
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };
  const [hasMoreImages, setHasMoreImages] = useState(true); // Track if more images are available
  const [currentPage, setCurrentPage] = useState(0); // Track current page for pagination
  const PAGE_SIZE = 32; // Number of images to fetch per page
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  // States for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<GeneratedImage | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  // Mobile filter states
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // Timeframe options for the filter
  type TimeframeOption =
    | "all"
    | "today"
    | "last7days"
    | "last30days"
    | "last90days";

  const [selectedTimeframe, setSelectedTimeframe] =
    useState<TimeframeOption>("last30days");
  const [onlyFavorites, setOnlyFavorites] = useState<boolean>(false);
  type ContentTypeOption = "all" | "images" | "videos";
  const [contentTypeFilter, setContentTypeFilter] =
    useState<ContentTypeOption>("all");
  // Mobile drafts for filters (apply only on button press)
  const [mobileSelectedTimeframe, setMobileSelectedTimeframe] =
    useState<TimeframeOption>("last30days");
  const [mobileSelectedVariant, setMobileSelectedVariant] = useState<
    number | string | null
  >(null);
  const [mobileOnlyFavorites, setMobileOnlyFavorites] =
    useState<boolean>(false);
  const [mobileContentType, setMobileContentType] =
    useState<ContentTypeOption>("all");
  const [searchQuery, setSearchQuery] = useState("");
  // Custom dropdown states (desktop)
  const [timeframeDropdownOpen, setTimeframeDropdownOpen] = useState(false);
  const [variantDropdownOpen, setVariantDropdownOpen] = useState(false);
  const [contentTypeDropdownOpen, setContentTypeDropdownOpen] = useState(false);
  const timeframeDropdownRef = useRef<HTMLDivElement>(null);
  const variantDropdownRef = useRef<HTMLDivElement>(null);
  const contentTypeDropdownRef = useRef<HTMLDivElement>(null);
  // iOS video blob prefetch cache (keyed by image ID) — stores Promises so in-flight fetches can be awaited
  const videoBlobCacheRef = useRef<Map<number, Promise<Blob>>>(new Map());
  // Resolved blobs ready for instant share (keyed by image ID)
  const videoResolvedBlobRef = useRef<Map<number, Blob>>(new Map());
  // Pending video share state: when blob isn't ready at tap time, we show a "Save" button
  const [pendingVideoShare, setPendingVideoShare] = useState<{
    imageId: number;
    fileName: string;
  } | null>(null);

  // "Más" popover in lightbox
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuMobileRef = useRef<HTMLDivElement>(null);
  const moreMenuDesktopRef = useRef<HTMLDivElement>(null);
  // "Más" popover in mobile detail view
  const [showMobileDetailMore, setShowMobileDetailMore] = useState(false);
  const mobileDetailMoreRef = useRef<HTMLDivElement>(null);
  // Mobile filter panel drag state
  const [filterMenuAnimated, setFilterMenuAnimated] = useState(false);
  const [filterMenuDragY, setFilterMenuDragY] = useState(0);
  const filterMenuDragStartRef = useRef(0);

  // Track which images have already been refreshed to avoid infinite loops
  const refreshedImagesRef = useRef<Set<number>>(new Set());

  // Function to refresh signed URL for a specific image when it expires
  const refreshImageUrl = async (image: GeneratedImage) => {
    if (!companyId || !image.file_name) return;

    // Avoid infinite refresh loops - only try once per image per session
    if (refreshedImagesRef.current.has(image.id)) {
      console.warn(`Image ${image.id} already refreshed, skipping`);
      return;
    }
    refreshedImagesRef.current.add(image.id);

    try {
      const isVideo =
        image.tipo === "video" ||
        image.tipo === "video_pro" ||
        image.file_name.toLowerCase().endsWith(".mp4");

      const fileName = image.file_name;
      const thumbnailFileName = isVideo
        ? fileName.replace(/\.(mp4|webm|mov)$/i, ".png")
        : fileName;

      const optFilePath = `${companyId}/opt/${thumbnailFileName}`;
      const fullFilePath = `${companyId}/full/${fileName}`;

      let newSignedUrl = "";
      let newThumbnailUrl = "";

      // Fetch opt + full in parallel
      const [optRes, fullRes] = await Promise.all([
        supabase.storage.from("generadas").createSignedUrl(optFilePath, 3600),
        supabase.storage.from("generadas").createSignedUrl(fullFilePath, 3600),
      ]);

      if (optRes.data?.signedUrl) {
        newThumbnailUrl = optRes.data.signedUrl;
        setCachedSignedUrl(optFilePath, newThumbnailUrl);
        if (fullRes.data?.signedUrl) {
          newSignedUrl = fullRes.data.signedUrl;
          setCachedSignedUrl(fullFilePath, newSignedUrl);
        } else if (!isVideo) {
          newSignedUrl = newThumbnailUrl;
        }
      } else if (fullRes.data?.signedUrl) {
        newSignedUrl = fullRes.data.signedUrl;
        newThumbnailUrl = fullRes.data.signedUrl;
        setCachedSignedUrl(fullFilePath, newSignedUrl);
      }

      if (newSignedUrl || newThumbnailUrl) {
        // Update the image in state with new URLs
        setAllImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                  ...img,
                  signedUrl: newSignedUrl || img.signedUrl,
                  thumbnailUrl: newThumbnailUrl || img.thumbnailUrl,
                }
              : img,
          ),
        );
        setFilteredImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                  ...img,
                  signedUrl: newSignedUrl || img.signedUrl,
                  thumbnailUrl: newThumbnailUrl || img.thumbnailUrl,
                }
              : img,
          ),
        );
        if (selectedImage?.id === image.id) {
          setSelectedImage((prev) =>
            prev
              ? {
                  ...prev,
                  signedUrl: newSignedUrl || prev.signedUrl,
                  thumbnailUrl: newThumbnailUrl || prev.thumbnailUrl,
                }
              : null,
          );
        }
      }
    } catch (err) {
      console.error(`Error refreshing URL for image ${image.id}:`, err);
    }
  };

  // Lazily fetch the full signed URL for an image when user opens it
  const ensureFullUrl = async (image: GeneratedImage) => {
    if (!companyId || !image.file_name) return;
    // Already has a full URL that is different from thumbnail — nothing to do
    if (image.signedUrl && image.signedUrl !== image.thumbnailUrl) return;

    const fileName = image.file_name;
    const fullFilePath = `${companyId}/full/${fileName}`
      .replace(/\/+/, "/")
      .replace(/\/+/g, "/");

    // Check cache first
    const cached = getCachedSignedUrl(fullFilePath);
    if (cached) {
      const update = (img: GeneratedImage) =>
        img.id === image.id ? { ...img, signedUrl: cached } : img;
      setAllImages((prev) => prev.map(update));
      setFilteredImages((prev) => prev.map(update));
      setSelectedImage((prev) =>
        prev?.id === image.id ? { ...prev, signedUrl: cached } : prev,
      );
      setCurrentViewImage((prev) =>
        prev?.id === image.id ? { ...prev, signedUrl: cached } : prev,
      );
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from("generadas")
        .createSignedUrl(fullFilePath, 3600);
      if (!error && data?.signedUrl) {
        setCachedSignedUrl(fullFilePath, data.signedUrl);
        const update = (img: GeneratedImage) =>
          img.id === image.id ? { ...img, signedUrl: data.signedUrl } : img;
        setAllImages((prev) => prev.map(update));
        setFilteredImages((prev) => prev.map(update));
        setSelectedImage((prev) =>
          prev?.id === image.id ? { ...prev, signedUrl: data.signedUrl } : prev,
        );
        setCurrentViewImage((prev) =>
          prev?.id === image.id ? { ...prev, signedUrl: data.signedUrl } : prev,
        );
      }
    } catch (err) {
      console.error(`Error fetching full URL for image ${image.id}:`, err);
    }
  };

  // Lazily fetch the upscaled signed URL if the image has an upscaled version
  const ensureUpscaledUrl = async (image: GeneratedImage) => {
    if (!image.upscaled_path || image.upscaledSignedUrl) return;
    try {
      const cached = getCachedSignedUrl(image.upscaled_path);
      if (cached) {
        const update = (img: GeneratedImage) =>
          img.id === image.id ? { ...img, upscaledSignedUrl: cached } : img;
        setAllImages((prev) => prev.map(update));
        setFilteredImages((prev) => prev.map(update));
        setSelectedImage((prev) =>
          prev?.id === image.id ? { ...prev, upscaledSignedUrl: cached } : prev,
        );
        setCurrentViewImage((prev) =>
          prev?.id === image.id ? { ...prev, upscaledSignedUrl: cached } : prev,
        );
        return;
      }
      const { data } = await supabase.storage
        .from("generadas")
        .createSignedUrl(image.upscaled_path, 3600);
      if (data?.signedUrl) {
        setCachedSignedUrl(image.upscaled_path, data.signedUrl);
        const update = (img: GeneratedImage) =>
          img.id === image.id
            ? { ...img, upscaledSignedUrl: data.signedUrl }
            : img;
        setAllImages((prev) => prev.map(update));
        setFilteredImages((prev) => prev.map(update));
        setSelectedImage((prev) =>
          prev?.id === image.id
            ? { ...prev, upscaledSignedUrl: data.signedUrl }
            : prev,
        );
        setCurrentViewImage((prev) =>
          prev?.id === image.id
            ? { ...prev, upscaledSignedUrl: data.signedUrl }
            : prev,
        );
      }
    } catch (err) {
      console.error(`Error fetching upscaled URL for image ${image.id}:`, err);
    }
  };

  // Calculate date range based on selected timeframe.
  // "all" devuelve null para indicar "sin filtro de fecha".
  const getDateRangeFromTimeframe = (
    timeframe: TimeframeOption,
  ): { start: string; end: string } | null => {
    if (timeframe === "all") return null;

    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999); // End of current day

    let start = new Date(now);

    switch (timeframe) {
      case "today":
        start.setHours(0, 0, 0, 0); // Start of current day
        break;
      case "last7days":
        start.setDate(start.getDate() - 6); // Last 7 days (today + 6 previous days)
        start.setHours(0, 0, 0, 0);
        break;
      case "last30days":
        start.setDate(start.getDate() - 29); // Last 30 days (today + 29 previous days)
        start.setHours(0, 0, 0, 0);
        break;
      case "last90days":
      default:
        start.setDate(start.getDate() - 89); // Last 90 days (today + 89 previous days)
        start.setHours(0, 0, 0, 0);
        break;
    }

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  };


  // Determine if Instagram is connected using company schema instagram_data
  const checkInstagramConnection = async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setIsInstagramConnected(false);
        return;
      }

      // Check company schema instagram_data (single source of truth)
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (profile?.id_company) {
        const { data: instagramData } = await supabase
          .from("instagram_data")
          .select("instagram_username")
          .eq("id_company", profile.id_company)
          .maybeSingle();

        if (instagramData?.instagram_username) {
          setIsInstagramConnected(true);
          return;
        }
      }

      setIsInstagramConnected(false);
    } catch (e) {
      console.error("Error checking Instagram connection:", e);
      setIsInstagramConnected(false);
    }
  };

  // Initialize date range from selected timeframe (use restored timeframe if available)
  const [dateRange, setDateRange] = useState(
    getDateRangeFromTimeframe(selectedTimeframe),
  );
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(
    null,
  );
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // Desktop magnifier lens state
  const [magnifier, setMagnifier] = useState<{
    show: boolean;
    x: number;
    y: number;
    bgX: number;
    bgY: number;
  }>({ show: false, x: 0, y: 0, bgX: 0, bgY: 0 });
  const desktopImgRef = useRef<HTMLImageElement>(null);
  const magnifierTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Lightbox open/close animation
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const closeLightbox = () => {
    setLightboxOpen(false);
    setShowCropModal(false);
    setTimeout(() => {
      setSelectedImage(null);
      setMagnifier((m) => ({ ...m, show: false }));
    }, 200);
  };
  const [loadingThumbnails, setLoadingThumbnails] = useState<
    Record<number, boolean>
  >({});

  // Auto-expand image when returning from carrusel
  const expandHandledRef = useRef(false);
  useEffect(() => {
    if (
      expandImageIdFromState &&
      !expandHandledRef.current &&
      filteredImages.length > 0
    ) {
      expandHandledRef.current = true;
      const match = filteredImages.find(
        (img) => String(img.id) === String(expandImageIdFromState),
      );
      if (match) {
        setSelectedImage(match);
        setLightboxOpen(true);
      }
      // Clear the state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [expandImageIdFromState, filteredImages]);
  const [expandedImageLoading, setExpandedImageLoading] = useState(false);

  // Instagram posting states
  const [showInstagramPreview, setShowInstagramPreview] =
    useState<boolean>(false);

  // Debug log: Compare variant ids and image variante_ids
  useEffect(() => {
    if (allImages.length > 0 && variants.length > 0) {
      console.log(
        "DEBUG - Variants in dropdown:",
        variants.map((v) => v.id),
      );
      console.log(
        "DEBUG - Images variante_id:",
        allImages.map((img) => img.variante_id),
      );
    }
  }, [allImages, variants]);
  const instagramUsername = "usuario_instagram";
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isPostingToInstagram, setIsPostingToInstagram] = useState(false);
  const [instagramPostSuccess, setInstagramPostSuccess] = useState(false);
  const [postDescription, setPostDescription] = useState(
    "Moda fashion . veni a visitarnos",
  );
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(postDescription);
  const imageContainerRef = useRef<HTMLImageElement>(null);
  const [isInstagramConnected, setIsInstagramConnected] =
    useState<boolean>(false);
  const [showConnectPrompt, setShowConnectPrompt] = useState<boolean>(false);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] =
    useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<
    "free-trial-ended" | "plan-limit-reached"
  >("free-trial-ended");
  const [showEditPlanRestriction, setShowEditPlanRestriction] =
    useState<boolean>(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [userCountry, setUserCountry] = useState<string>("");
  const isArgentina = (userCountry || "").toLowerCase() === "argentina";
  const [openingBillingPortal, setOpeningBillingPortal] =
    useState<boolean>(false);

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningBillingPortal(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");
      const resp = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/billing-portal",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({ user_id: user.id }),
        },
      );
      if (!resp.ok)
        throw new Error("No se pudo abrir el portal de facturación");
      const data = await resp.json();
      if (data?.url) {
        window.location.href = data.url as string;
      } else {
        throw new Error("URL inválida recibida");
      }
    } catch (e: any) {
      alert(e?.message || "Error al abrir el portal de facturación");
      setOpeningBillingPortal(false);
    }
  };
  const [showDescriptionPanel, setShowDescriptionPanel] =
    useState<boolean>(false);
  const [copiedPanel, setCopiedPanel] = useState<boolean>(false);
  const [copiedPreview, setCopiedPreview] = useState<boolean>(false);
  const [progressActive, setProgressActive] = useState<boolean>(false);

  // ─── Mobile detail view (Apple Photos style) ───
  const [mobileView, setMobileView] = useState<"grid" | "detail">("grid");
  const [currentViewImage, setCurrentViewImage] =
    useState<GeneratedImage | null>(null);

  // ─── Crop state ───
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [isDesktopCrop, setIsDesktopCrop] = useState(
    () => window.matchMedia("(min-width: 640px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktopCrop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  // Resolve full-resolution URL for crop (mobile uses currentViewImage, desktop uses selectedImage)
  const cropTargetImage = isDesktopCrop ? selectedImage : currentViewImage;
  const [cropFullUrl, setCropFullUrl] = useState("");
  useEffect(() => {
    if (!showCropModal || !cropTargetImage || !companyId) {
      setCropFullUrl("");
      return;
    }
    // If signedUrl contains /full/ it's already the full version
    if (
      cropTargetImage.signedUrl &&
      cropTargetImage.signedUrl.includes("/full/")
    ) {
      setCropFullUrl(cropTargetImage.signedUrl);
      return;
    }
    // Otherwise fetch full version
    const fullPath = `${companyId}/full/${cropTargetImage.file_name}`;
    const cached = getCachedSignedUrl(fullPath);
    if (cached) {
      setCropFullUrl(cached);
    } else {
      supabase.storage
        .from("generadas")
        .createSignedUrl(fullPath, 3600)
        .then(({ data }) => {
          if (data?.signedUrl) {
            setCachedSignedUrl(fullPath, data.signedUrl);
            setCropFullUrl(data.signedUrl);
          }
        });
    }
  }, [showCropModal, cropTargetImage, companyId]);

  // Fetch user plan + country (used for upgrade flow branching)
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: company } = await supabase
          .from("company_profiles")
          .select("plan, pais")
          .eq("id_company", companyId)
          .single();
        if (cancelled) return;
        if ((company as any)?.plan) setUserPlan((company as any).plan as string);
        if ((company as any)?.pais)
          setUserCountry((company as any).pais as string);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);
  const cropState = useCropState(
    cropFullUrl,
    cropTargetImage?.file_name || "",
    () => setShowCropModal(false),
  );

  // Mobile scroll container ref + saved scroll position
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const savedGridScrollTop = useRef<number>(0);
  // Hero animation state
  const heroNaturalSize = useRef<{ w: number; h: number } | null>(null);
  const [heroRect, setHeroRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [heroAnimating, setHeroAnimating] = useState<boolean>(false);
  const [heroPhase, setHeroPhase] = useState<"start" | "end">("start");
  const [heroDirection, setHeroDirection] = useState<"forward" | "reverse">(
    "forward",
  );
  const [heroTargetRect, setHeroTargetRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  // Scroll-based swipe for detail view
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollProgrammatic = useRef<boolean>(false);
  const heroImageRef = useRef<HTMLDivElement>(null);

  // Fetch company logo
  const fetchCompanyLogo = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (profile?.id_company) {
        const { data: companyData } = await supabase
          .from("company_profiles")
          .select("logo_url")
          .eq("id_company", profile.id_company)
          .single();

        if (companyData?.logo_url) {
          setCompanyLogo(companyData.logo_url);
        }
      }
    } catch (err) {
      console.error("Error fetching company logo:", err);
    }
  };

  // Function to generate a description for the Instagram post
  const generateDescription = async () => {
    if (!selectedImage) return;

    setIsGeneratingDescription(true);
    setPostDescription("Generando descripción...");

    try {
      // Get the user from Supabase auth
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("No se pudo identificar al usuario");
        setPostDescription("Moda fashion . veni a visitarnos");
        setIsGeneratingDescription(false);
        return;
      }

      const response = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: user.id,
            imagen_id: selectedImage.id,
            mode: "generate_description",
          }),
        },
      );

      const data = await response.json();

      if (data.response === "success") {
        // Find company ID for the schema
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();

        if (!profile?.id_company) {
          alert("No se pudo identificar la empresa");
          setPostDescription("Moda fashion . veni a visitarnos");
          setIsGeneratingDescription(false);
          return;
        }

        // Fetch the generated description from the database
        const { data: imageData, error } = await supabase
          .from("imagenes_generadas")
          .select("post_description")
          .eq("id", selectedImage.id)
          .eq("id_company", profile.id_company)
          .single();

        if (error) {
          console.error("Error fetching the description:", error);
          alert("Error al obtener la descripción generada");
          setPostDescription("Moda fashion . veni a visitarnos");
        } else if (imageData?.post_description) {
          setPostDescription(imageData.post_description);
        } else {
          setPostDescription("Moda fashion . veni a visitarnos");
        }
      } else {
        alert("Error al generar la descripción");
        setPostDescription("Moda fashion . veni a visitarnos");
      }
    } catch (error) {
      console.error("Error generating description:", error);
      alert("Error al generar la descripción");
      setPostDescription("Moda fashion . veni a visitarnos");
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Reset description panel and loading state when navigating to another image
  useEffect(() => {
    setShowDescriptionPanel(false);
    setIsGeneratingDescription(false);
    setPostDescription("");
    setExpandedImageLoading(true); // Show loading spinner immediately when changing image
  }, [selectedImage?.id]);

  // Trigger non-linear bottom progress bar when generating description
  useEffect(() => {
    if (showDescriptionPanel && isGeneratingDescription) {
      setProgressActive(false);
      const raf = requestAnimationFrame(() => setProgressActive(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setProgressActive(false);
    }
  }, [showDescriptionPanel, isGeneratingDescription]);

  // Function to post to Instagram
  const handlePostToInstagram = async () => {
    if (!selectedImage) return;

    setIsPostingToInstagram(true);
    setInstagramPostSuccess(false);

    try {
      // Get the user from Supabase auth
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("No se pudo identificar al usuario");
        setIsPostingToInstagram(false);
        return;
      }

      const response = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: user.id,
            imagen_id: selectedImage.id,
            mode: "post",
          }),
        },
      );

      const data = await response.json();

      if (data.response === "success") {
        setInstagramPostSuccess(true);
        // No need for alert as we'll show the visual success message
      } else {
        alert("Error al publicar en Instagram");
      }
    } catch (error) {
      console.error("Error posting to Instagram:", error);
      alert("Error al publicar en Instagram");
    } finally {
      setIsPostingToInstagram(false);
    }
  };

  // Copy Instagram post description to clipboard
  const copyDescription = async (scope: "panel" | "preview" = "panel") => {
    try {
      await navigator.clipboard.writeText(postDescription);
      if (scope === "panel") {
        setCopiedPanel(true);
        setTimeout(() => setCopiedPanel(false), 2000);
      } else {
        setCopiedPreview(true);
        setTimeout(() => setCopiedPreview(false), 2000);
      }
    } catch (error) {
      console.error("Error al copiar la descripción:", error);
      alert("No se pudo copiar la descripción");
    }
  };

  // Poll DB for post_description after webhook returns success (handles async update)
  const waitForPostDescription = async (
    idCompany: string,
    imageId: number,
    maxWaitMs = 15000,
    intervalMs = 800,
  ): Promise<string | null> => {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      try {
        // Abort if user navigated to another image
        if (!selectedImage || selectedImage.id !== imageId) return null;
        const { data: imageData } = await supabase
          .from("imagenes_generadas")
          .select("post_description")
          .eq("id", imageId)
          .eq("id_company", idCompany)
          .single();
        const desc = (imageData as any)?.post_description as string | undefined;
        if (desc && desc.trim().length > 0) {
          return desc;
        }
      } catch {}
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return null;
  };

  const handleShowDescription = async (
    e?: React.MouseEvent,
    targetImage?: GeneratedImage | null,
  ) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const imageToDescribe = targetImage || selectedImage;
    if (!imageToDescribe) return;
    setShowDescriptionPanel(true);
    setCopiedPanel(false);
    setIsGeneratingDescription(true);
    setPostDescription("Generando descripción para post de Instagram");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setPostDescription(
          "No se pudo generar la descripción. Intentá nuevamente.",
        );
        setIsGeneratingDescription(false);
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      let idCompany = (profile as any)?.id_company ?? null;
      if (!idCompany && companyId) {
        idCompany = companyId;
      }
      if (!idCompany) {
        setPostDescription(
          "No se pudo generar la descripción. Intentá nuevamente.",
        );
        setIsGeneratingDescription(false);
        return;
      }
      const { data: existing } = await supabase
        .from("imagenes_generadas")
        .select("post_description")
        .eq("id", imageToDescribe.id)
        .eq("id_company", idCompany)
        .single();
      if ((existing as any)?.post_description) {
        setPostDescription((existing as any).post_description as string);
        setIsGeneratingDescription(false);
        return;
      }
      const response = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/descripcion_instagram",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({
            user_id: user.id,
            image_id: imageToDescribe.id,
          }),
        },
      );
      let ok = false;
      try {
        const data = await response.json();
        ok = data?.response === "success";
      } catch {
        ok = false;
      }
      if (ok) {
        const finalText = await waitForPostDescription(
          idCompany,
          imageToDescribe.id,
        );
        if (finalText) {
          setPostDescription(finalText);
        } else {
          setPostDescription(
            "No se pudo obtener la descripción todavía. Probá nuevamente en unos segundos.",
          );
        }
      } else {
        setPostDescription(
          "No se pudo generar la descripción. Intentá nuevamente.",
        );
      }
    } catch (_e) {
      setPostDescription(
        "No se pudo generar la descripción. Intentá nuevamente.",
      );
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Handle clicking the Publicar button to generate description and show Instagram preview
  const handlePostButton = (image: GeneratedImage | null) => {
    if (!image) return;

    console.log("Post selected to Instagram:", image.id);

    // Set this image as the current selected image
    setSelectedImage(image);

    // Show Instagram preview
    setShowInstagramPreview(true);

    // Generate description for this image
    if (!instagramPostSuccess) {
      generateDescription();
    }
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();
    const daysInMonth = (year: number, month: number) =>
      new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(
        subscriptionDate.getFullYear(),
        subscriptionDate.getMonth(),
        subscriptionDate.getDate(),
        0,
        0,
        0,
        0,
      );
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const checkSubscriptionLimitsForEdit = async (): Promise<boolean> => {
    try {
      if (!companyId) return true;
      const { data: companyProfile } = await supabase
        .from("company_profiles")
        .select("plan, imagenes_restantes, paid_plan_start_date")
        .eq("id_company", companyId)
        .single();
      if (!companyProfile) return true;
      const remaining = (companyProfile as any).imagenes_restantes as
        | number
        | null
        | undefined;
      if (typeof remaining === "number" && remaining <= 0) {
        const isFree = (companyProfile as any).plan === "gratis";
        setSubscriptionLimitType(
          isFree ? "free-trial-ended" : "plan-limit-reached",
        );
        setShowSubscriptionLimitModal(true);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  const handleEditImage = async (
    image: GeneratedImage | null,
    e?: React.MouseEvent,
  ) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!image || !companyId) return;
    const canEdit = await checkSubscriptionLimitsForEdit();
    if (!canEdit) return;
    try {
      trackInteraction("imagenes_editadas");
    } catch {}
    // Save state so we can restore when user comes back
    saveHistoryState({
      selectedTimeframe,
      selectedVariant,
      onlyFavorites,
      searchQuery,
      scrollTop: mobileScrollRef.current?.scrollTop ?? window.scrollY ?? 0,
      currentViewImageId: image.id,
      mobileView: mobileView as "grid" | "detail",
    });
    // Navigate to video editor if it's a video, otherwise image editor
    if (isVideoItem(image)) {
      navigate(
        `/redes-sociales/editar-video?id=${image.id}&companyId=${companyId}`,
      );
    } else {
      navigate(
        `/redes-sociales/editar-imagen?id=${image.id}&companyId=${companyId}`,
      );
    }
  };

  // Use a ref to track initialization
  const initialLoadRef = useRef(false);
  const filterChangeRef = useRef<{
    variant: number | string | null;
    onlyFavorites: boolean;
    timeframe: typeof selectedTimeframe;
    brandId: string | null;
  }>({
    variant: selectedVariant,
    onlyFavorites,
    timeframe: selectedTimeframe,
    brandId: selectedBrandId,
  });

  // Create ref for infinite scroll observation
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Fetch images and Instagram data on component mount
  // Also restore filters from sessionStorage if returning from EditarImagen
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      // Check if we have saved state from before navigating to EditarImagen
      const saved = loadHistoryState();
      if (saved) {
        // Store for later use (scroll + image restore after load)
        pendingRestore.current = saved;
        // Apply filter state to UI
        setSelectedTimeframe(saved.selectedTimeframe);
        setSelectedVariant(saved.selectedVariant);
        setOnlyFavorites(saved.onlyFavorites);
        setSearchQuery(saved.searchQuery);
        setMobileSelectedTimeframe(saved.selectedTimeframe);
        setMobileSelectedVariant(saved.selectedVariant);
        setMobileOnlyFavorites(saved.onlyFavorites);
        const restoredDateRange = getDateRangeFromTimeframe(
          saved.selectedTimeframe,
        );
        setDateRange(restoredDateRange);
        // Update filterChangeRef so the filter-change effect doesn't double-fetch
        filterChangeRef.current = {
          variant: saved.selectedVariant,
          onlyFavorites: saved.onlyFavorites,
          timeframe: saved.selectedTimeframe,
          brandId: selectedBrandId,
        };
        // Clear sessionStorage
        clearHistoryState();
        // Fetch with restored filters (setState hasn't committed yet)
        fetchImages(false, {
          variant: saved.selectedVariant,
          onlyFavorites: saved.onlyFavorites,
          dateRange: restoredDateRange,
        });
      } else {
        fetchImages();
      }
      fetchCompanyLogo();
      checkInstagramConnection();
      try {
        trackInteraction("historial");
      } catch {}
    }
  }, []);

  // Restore scroll position after images load (returning from EditarImagen)
  useEffect(() => {
    if (loading || !pendingRestore.current) return;
    const saved = pendingRestore.current;
    pendingRestore.current = null; // consume once

    const scrollTop = saved.scrollTop;
    // Just restore scroll position — don't re-open any image
    requestAnimationFrame(() => {
      if (window.innerWidth < 640 && mobileScrollRef.current) {
        mobileScrollRef.current.scrollTop = scrollTop;
      } else {
        window.scrollTo(0, scrollTop);
      }
    });
  }, [loading, allImages, filteredImages]);

  // Sync mobile drafts when opening the mobile filters panel
  useEffect(() => {
    if (showMobileFilters) {
      setMobileSelectedTimeframe(selectedTimeframe);
      setMobileSelectedVariant(selectedVariant);
      setMobileOnlyFavorites(onlyFavorites);
      setMobileContentType(contentTypeFilter);
      setMobileSelectedBrandId(selectedBrandId);
    }
  }, [
    showMobileFilters,
    selectedTimeframe,
    selectedVariant,
    onlyFavorites,
    contentTypeFilter,
  ]);

  // Handle filter changes separately to avoid loops
  useEffect(() => {
    // Skip the first render
    if (initialLoadRef.current) {
      // Check if filters actually changed
      const prevFilters = filterChangeRef.current;
      if (
        prevFilters.variant !== selectedVariant ||
        prevFilters.onlyFavorites !== onlyFavorites ||
        prevFilters.timeframe !== selectedTimeframe ||
        prevFilters.brandId !== selectedBrandId
      ) {
        // Update the ref with new values
        filterChangeRef.current = {
          variant: selectedVariant,
          onlyFavorites,
          timeframe: selectedTimeframe,
          brandId: selectedBrandId,
        };

        // Compute fresh date range from the current timeframe to avoid stale state
        const freshDateRange = getDateRangeFromTimeframe(selectedTimeframe);
        setDateRange(freshDateRange);

        // Reset pagination when filters change
        setCurrentPage(0);
        setAllImages([]);
        setFilteredImages([]);
        setHasMoreImages(true); // Assume there are more images until we check
        fetchImages(false, {
          variant: selectedVariant,
          onlyFavorites,
          dateRange: freshDateRange,
          brandId: selectedBrandId,
        });
      }
    }
  }, [selectedVariant, onlyFavorites, selectedTimeframe, selectedBrandId]);

  const fetchImages = async (
    isLoadingMore = false,
    filterOverrides?: {
      variant?: number | string | null;
      onlyFavorites?: boolean;
      dateRange?: { start: string; end: string } | null;
      brandId?: string | null;
    },
  ) => {
    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Try cached profile first
      type ProfileCache = { id_company: string; name_short: string };
      let profileData = getCached<ProfileCache>(CACHE_KEYS.USER_PROFILE);
      let idCompany: string;

      if (profileData) {
        idCompany = profileData.id_company;
      } else {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No user found");
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company, name_short")
          .eq("id", user.id)
          .single();
        const idComp = (profile as any)?.id_company ?? null;
        if (!idComp) throw new Error("No company associated with user");
        idCompany = idComp;
        setCached(CACHE_KEYS.USER_PROFILE, {
          id_company: idComp,
          name_short: (profile as any).name_short || "",
        });
      }

      setCompanyId(idCompany);
      setCompanyUuid(idCompany);

      // Fetch variants first if not already loaded
      if (variants.length === 0) {
        const { data: variantsData } = await supabase
          .from("variantes")
          .select("id, nombre")
          .eq("id_company", idCompany)
          .order("nombre");
        setVariants(variantsData || []);
      }

      // Fetch brands (solo se usa el filtro si hay >1 marca)
      if (brands.length === 0) {
        const { data: brandsData } = await supabase
          .from("brand_profiles")
          .select("id, name, logo_url, is_default")
          .eq("id_company", idCompany)
          .eq("is_draft", false)
          .order("is_default", { ascending: false })
          .order("created_at", { ascending: true });
        setBrands((brandsData as BrandRow[] | null) ?? []);
      }

      // Use overrides if provided, otherwise use current state
      const effectiveVariant =
        filterOverrides?.variant !== undefined
          ? filterOverrides.variant
          : selectedVariant;
      const effectiveFavorites =
        filterOverrides?.onlyFavorites !== undefined
          ? filterOverrides.onlyFavorites
          : onlyFavorites;
      const effectiveDateRange =
        filterOverrides?.dateRange !== undefined
          ? filterOverrides.dateRange
          : dateRange;
      const effectiveBrandId =
        filterOverrides?.brandId !== undefined
          ? filterOverrides.brandId
          : selectedBrandId;

      // Build the query for images with pagination and filters.
      // Cuando hay filtro de marca, usamos inner-join hasta productos para filtrar
      // por productos.brand_id. Si no, embed normal con variantes(nombre).
      const selectStr = effectiveBrandId
        ? `
          id,
          file_name,
          created_at,
          variante_id,
          formato,
          tipo,
          favorito,
          active,
          upscaled_path,
          variantes:variantes!inner(nombre, productos!inner(brand_id))
        `
        : `
          id,
          file_name,
          created_at,
          variante_id,
          formato,
          tipo,
          favorito,
          active,
          upscaled_path,
          variantes:variantes(nombre)
        `;
      let query = supabase
        .from("imagenes_generadas")
        .select(selectStr)
        .eq("id_company", idCompany)
        .eq("active", true)
        .order("created_at", { ascending: false });

      // Server-side filters
      if (effectiveVariant !== null) {
        query = query.eq("variante_id", effectiveVariant);
      }
      if (effectiveBrandId) {
        query = query.eq("variantes.productos.brand_id", effectiveBrandId);
      }
      if (effectiveFavorites) {
        query = query.eq("favorito", true);
      }
      if (
        effectiveDateRange &&
        effectiveDateRange.start &&
        effectiveDateRange.end
      ) {
        query = query
          .gte("created_at", effectiveDateRange.start)
          .lte("created_at", effectiveDateRange.end);
      }

      if (isLoadingMore) {
        // If loading more, use the current page to calculate the range
        const startIndex = currentPage * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE - 1;
        query = query.range(startIndex, endIndex);
      } else {
        // Initial load - get first page
        query = query.range(0, PAGE_SIZE - 1);
      }

      const { data: imagesData, error: imagesError } = await query;

      if (imagesError) throw imagesError;

      // Create loading state for thumbnails
      const loadingThumbnailsMap: Record<number, boolean> = {};

      // Process images — only fetch opt (thumbnail) URLs initially; full URLs are loaded on demand
      const processedImages = await Promise.all(
        (imagesData || []).map(async (img: any) => {
          loadingThumbnailsMap[img.id] = true;

          const isVideo =
            img.tipo === "video" ||
            img.tipo === "video_pro" ||
            (img.file_name && img.file_name.toLowerCase().endsWith(".mp4"));

          const basePrefix = `${idCompany}`;
          const fileName = img.file_name ?? "";
          const thumbnailFileName = isVideo
            ? fileName.replace(/\.(mp4|webm|mov)$/i, ".png")
            : fileName;

          const optFilePath = `${basePrefix}/opt/${thumbnailFileName}`
            .replace(/\/+/, "/")
            .replace(/\/+/g, "/");
          const fullFilePath = `${basePrefix}/full/${fileName}`
            .replace(/\/+/, "/")
            .replace(/\/+/g, "/");

          let signedUrl = "";
          let thumbnailUrl = "";

          try {
            // Use cached URLs if available (both opt and full)
            const cachedOpt = getCachedSignedUrl(optFilePath);
            const cachedFull = getCachedSignedUrl(fullFilePath);

            if (cachedOpt) {
              thumbnailUrl = cachedOpt;
            }
            if (cachedFull) {
              signedUrl = cachedFull;
            }

            // Only fetch opt if not cached — full is loaded on demand when user opens an image
            if (!cachedOpt) {
              const { data: optData, error: optError } = await supabase.storage
                .from("generadas")
                .createSignedUrl(optFilePath, 3600);
              if (!optError && optData?.signedUrl) {
                thumbnailUrl = optData.signedUrl;
                setCachedSignedUrl(optFilePath, thumbnailUrl);
              } else if (!isVideo) {
                // Opt not found for image — fall back to full for thumbnail
                if (!cachedFull) {
                  const { data: fullData } = await supabase.storage
                    .from("generadas")
                    .createSignedUrl(fullFilePath, 3600);
                  if (fullData?.signedUrl) {
                    signedUrl = fullData.signedUrl;
                    thumbnailUrl = fullData.signedUrl;
                    setCachedSignedUrl(fullFilePath, signedUrl);
                  }
                } else {
                  thumbnailUrl = cachedFull;
                }
              } else {
                // Video without opt — try .png thumbnail from full folder
                const fullThumbnailPath =
                  `${basePrefix}/full/${thumbnailFileName}`
                    .replace(/\/+/, "/")
                    .replace(/\/+/g, "/");
                const { data: videoThumbData } = await supabase.storage
                  .from("generadas")
                  .createSignedUrl(fullThumbnailPath, 3600);
                thumbnailUrl = videoThumbData?.signedUrl || "";
              }
            }
          } catch (error) {
            console.error("Error processing image paths:", error);
            signedUrl = "";
            thumbnailUrl = "";
          }

          return {
            id: img.id,
            file_name: img.file_name,
            created_at: img.created_at,
            variante_id: img.variante_id,
            variante_nombre: img.variante_id
              ? img.variantes?.nombre || ""
              : "Imagen",
            formato: img.formato || "",
            tipo: img.tipo || "",
            favorito: img.favorito === true,
            signedUrl,
            thumbnailUrl,
            upscaled_path: (img as any).upscaled_path ?? null,
          };
        }),
      );

      // Filter out non-video images that have no valid URLs (neither opt nor full loaded)
      const validImages = processedImages.filter((img) => {
        const isVideo = img.tipo === "video" || img.tipo === "video_pro";
        if (isVideo) return true; // Videos are handled with placeholder thumbnail
        return !!(img.signedUrl || img.thumbnailUrl);
      });

      // Check if we received less images than requested, meaning there are no more images to fetch
      setHasMoreImages(processedImages.length === PAGE_SIZE);

      // Process the images differently based on whether we're loading more or initial load
      if (isLoadingMore) {
        // If loading more, append the new images to existing ones
        setAllImages((prevImages) => [...prevImages, ...validImages]);
        setFilteredImages((prevImages) => [...prevImages, ...validImages]);
        // Update current page
        setCurrentPage((prev) => prev + 1);
      } else {
        // Initial load - replace all images
        setAllImages(validImages);
        setFilteredImages(validImages);
        // Set page to 1 since we've loaded the first page
        setCurrentPage(1);
      }

      // Check if there are potentially more images to load
      setHasMoreImages(processedImages.length === PAGE_SIZE);

      setLoadingThumbnails(loadingThumbnailsMap);
    } catch (err) {
      console.error("Error fetching images:", err);
      setError(err instanceof Error ? err.message : "Error loading images");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // The navigateImage function has been replaced with inline navigation in the arrow button click handlers

  // Silently prefetch video blob on iOS so share sheet works instantly on download tap
  // Stores Promise<Blob> keyed by image ID so handleDownload can await an in-flight fetch
  const prefetchVideoBlob = useCallback((image: GeneratedImage) => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isIOS || !isVideoItem(image)) return;
    const url = image.signedUrl;
    if (!url || videoBlobCacheRef.current.has(image.id)) return;
    const blobPromise = new Promise<Blob>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = "blob";
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response as Blob;
          videoResolvedBlobRef.current.set(image.id, blob);
          resolve(blob);
        } else {
          reject(new Error(`XHR failed: ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error("XHR network error"));
      xhr.send();
    });
    videoBlobCacheRef.current.set(image.id, blobPromise);
  }, []);

  // Re-attempt prefetch when selectedImage/currentViewImage signedUrl becomes available
  useEffect(() => {
    if (selectedImage) prefetchVideoBlob(selectedImage);
  }, [selectedImage?.signedUrl, prefetchVideoBlob]);
  useEffect(() => {
    if (currentViewImage) prefetchVideoBlob(currentViewImage);
  }, [currentViewImage?.signedUrl, prefetchVideoBlob]);

  const handleImageClick = (image: GeneratedImage) => {
    // Prevent default behavior to avoid unwanted navigation
    setSelectedImage(image);
    setZoomLevel(1);
    setDragOffset({ x: 0, y: 0 });
    prefetchVideoBlob(image);
    // Trigger open animation after a frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setLightboxOpen(true));
    });
  };

  // ─── Mobile detail view handlers (Apple Photos style) ───

  // Forward hero animation: grid → detail
  const handleGridImageTap = (
    image: GeneratedImage,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHeroRect({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    });
    const imgEl = event.currentTarget.querySelector("img");
    if (imgEl && imgEl.naturalWidth && imgEl.naturalHeight) {
      heroNaturalSize.current = {
        w: imgEl.naturalWidth,
        h: imgEl.naturalHeight,
      };
    } else {
      heroNaturalSize.current = null;
    }
    setCurrentViewImage(image);
    prefetchVideoBlob(image);
    setHeroPhase("start");
    setHeroDirection("forward");
    setHeroAnimating(true);
    if (mobileScrollRef.current) {
      savedGridScrollTop.current = mobileScrollRef.current.scrollTop;
    }
    setMobileView("detail");
    window.history.pushState({ mobileDetailView: true }, "");
    requestAnimationFrame(() => {
      // Scroll the detail snap container to the tapped image
      if (scrollContainerRef.current) {
        const idx = displayImagesRef.current.findIndex(
          (img) => img.id === image.id,
        );
        if (idx >= 0) {
          isScrollProgrammatic.current = true;
          scrollContainerRef.current.scrollTo({
            left: idx * scrollContainerRef.current.clientWidth,
            behavior: "instant" as ScrollBehavior,
          });
          requestAnimationFrame(() => {
            isScrollProgrammatic.current = false;
          });
        }
      }
      requestAnimationFrame(() => {
        let measured = false;
        // Try to measure the actual rendered <img> in the detail view (at the correct snap index)
        const scrollEl = scrollContainerRef.current;
        if (scrollEl) {
          const idx = displayImagesRef.current.findIndex(
            (img) => img.id === image.id,
          );
          const snapChild = idx >= 0 ? scrollEl.children[idx] : null;
          const detailImg = snapChild?.querySelector(
            "img",
          ) as HTMLImageElement | null;
          if (detailImg && detailImg.getBoundingClientRect().width > 0) {
            const r = detailImg.getBoundingClientRect();
            setHeroTargetRect({
              x: r.x,
              y: r.y,
              width: r.width,
              height: r.height,
            });
            measured = true;
          }
        }
        // Fallback: calculate contain-fit rect from natural size + viewer container
        if (!measured) {
          const viewerRect = heroImageRef.current?.getBoundingClientRect();
          const padding = 12;
          if (viewerRect) {
            const cW = viewerRect.width - padding * 2;
            const cH = viewerRect.height - padding * 2;
            const cTop = viewerRect.top + padding;
            const cLeft = viewerRect.left + padding;
            const ns = heroNaturalSize.current;
            if (ns) {
              const imgAspect = ns.w / ns.h;
              const cAspect = cW / cH;
              let eW: number, eH: number, eTop: number, eLeft: number;
              if (imgAspect > cAspect) {
                eW = cW;
                eH = cW / imgAspect;
                eLeft = cLeft;
                eTop = cTop + (cH - eH) / 2;
              } else {
                eH = cH;
                eW = cH * imgAspect;
                eTop = cTop;
                eLeft = cLeft + (cW - eW) / 2;
              }
              setHeroTargetRect({ x: eLeft, y: eTop, width: eW, height: eH });
            } else {
              setHeroTargetRect({ x: cLeft, y: cTop, width: cW, height: cH });
            }
          }
        }
        setHeroPhase("end");
        setTimeout(() => {
          setHeroAnimating(false);
          setHeroRect(null);
          setHeroTargetRect(null);
          setHeroDirection("forward");
        }, 420);
      });
    });
  };

  // Reverse hero animation: detail → grid
  const handleBackToGrid = () => {
    if (mobileView !== "detail") return;
    if (heroAnimating) return;
    setShowCropModal(false);
    const scrollEl = scrollContainerRef.current;
    let detailImg: HTMLImageElement | null = null;
    if (scrollEl) {
      const snapItems = scrollEl.children;
      const scrollLeft = scrollEl.scrollLeft;
      const itemWidth = scrollEl.clientWidth;
      const activeIndex = Math.round(scrollLeft / itemWidth);
      if (snapItems[activeIndex]) {
        detailImg = snapItems[activeIndex].querySelector(
          "img",
        ) as HTMLImageElement | null;
      }
    }
    const imgRect = detailImg?.getBoundingClientRect();
    if (imgRect && currentViewImage && imgRect.width > 0) {
      const startRect = {
        x: imgRect.x,
        y: imgRect.y,
        width: imgRect.width,
        height: imgRect.height,
      };
      setHeroRect(startRect);
      setHeroDirection("reverse");
      setHeroPhase("start");
      setHeroAnimating(true);
      requestAnimationFrame(() => {
        setMobileView("grid");
        requestAnimationFrame(() => {
          const thumbEl = document.querySelector(
            `[data-image-id="${currentViewImage!.id}"]`,
          );
          if (thumbEl && mobileScrollRef.current) {
            const container = mobileScrollRef.current;
            const thumbRect = thumbEl.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const isAbove = thumbRect.top < containerRect.top;
            const isBelow = thumbRect.bottom > containerRect.bottom;
            if (isAbove || isBelow) {
              const thumbCenter = thumbRect.top + thumbRect.height / 2;
              const containerCenter =
                containerRect.top + containerRect.height / 2;
              container.scrollTop += thumbCenter - containerCenter;
            }
          } else if (mobileScrollRef.current) {
            mobileScrollRef.current.scrollTop = savedGridScrollTop.current;
          }
          requestAnimationFrame(() => {
            const thumbElFinal = document.querySelector(
              `[data-image-id="${currentViewImage!.id}"]`,
            );
            if (thumbElFinal) {
              const thumbRect = thumbElFinal.getBoundingClientRect();
              setHeroTargetRect({
                x: thumbRect.x,
                y: thumbRect.y,
                width: thumbRect.width,
                height: thumbRect.height,
              });
            }
            setHeroPhase("end");
            setTimeout(() => {
              setHeroAnimating(false);
              setHeroRect(null);
              setHeroTargetRect(null);
              setHeroDirection("forward");
            }, 420);
          });
        });
      });
    } else {
      setMobileView("grid");
    }
  };

  const handleBackToGridRef = useRef(handleBackToGrid);
  handleBackToGridRef.current = handleBackToGrid;

  useEffect(() => {
    const handlePopState = () => {
      handleBackToGridRef.current();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Auto-fetch full signed URL when the viewed image changes (desktop lightbox or mobile detail)
  useEffect(() => {
    if (selectedImage) {
      ensureFullUrl(selectedImage);
      ensureUpscaledUrl(selectedImage);
    }
  }, [selectedImage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentViewImage) {
      ensureFullUrl(currentViewImage);
      ensureUpscaledUrl(currentViewImage);
    }
  }, [currentViewImage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll-snap handler: update currentViewImage when swiping between images
  const handleDetailScroll = useCallback(() => {
    if (isScrollProgrammatic.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const itemWidth = el.clientWidth;
    if (itemWidth === 0) return;
    const activeIndex = Math.round(scrollLeft / itemWidth);
    if (activeIndex >= 0 && activeIndex < displayImagesRef.current.length) {
      const img = displayImagesRef.current[activeIndex];
      if (img && img.id !== currentViewImage?.id) {
        setCurrentViewImage(img);
      }
    }
  }, [currentViewImage?.id]);

  // ─── Carrusel: confirmation modal + loading screen + API call + navigate ───
  const [showCarruselLoading, setShowCarruselLoading] =
    useState<boolean>(false);
  const [showCarruselConfirm, setShowCarruselConfirm] =
    useState<boolean>(false);
  const carruselLockRef = useRef<boolean>(false);
  const pendingCarruselImageRef = useRef<GeneratedImage | null>(null);

  const handleCarruselClick = async (imageOverride?: GeneratedImage) => {
    const targetImage = imageOverride || currentViewImage;
    if (!targetImage || !companyId || carruselLockRef.current) return;
    pendingCarruselImageRef.current = targetImage;

    try {
      const { data: cpData } = await supabase
        .from("company_profiles")
        .select("plan, imagenes_restantes")
        .eq("id_company", companyId)
        .single();
      const remaining = (cpData as any)?.imagenes_restantes as
        | number
        | null
        | undefined;
      if (typeof remaining === "number" && remaining <= 0) {
        const isFree = (cpData as any)?.plan === "gratis";
        setSubscriptionLimitType(
          isFree ? "free-trial-ended" : "plan-limit-reached",
        );
        setShowSubscriptionLimitModal(true);
        return;
      }
    } catch {
      // subscription check failed, allow modal to show anyway
    }

    setShowCarruselConfirm(true);
  };

  const handleGenerateNewAngles = async () => {
    const targetImage = pendingCarruselImageRef.current;
    if (!targetImage || !companyId || carruselLockRef.current) return;
    setShowCarruselConfirm(false);
    carruselLockRef.current = true;
    setShowCarruselLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        alert("No se pudo identificar al usuario");
        return;
      }

      // Fire API
      const response = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/generar_nuevos_angulos",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({ user_id: user.id, image_id: targetImage.id }),
        },
      );
      let newImageIds: string[] = [];
      try {
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.imagenes_generadas_ids) {
          newImageIds = data[0].imagenes_generadas_ids.map(String);
        } else if (data?.imagenes_generadas_ids) {
          newImageIds = data.imagenes_generadas_ids.map(String);
        }
      } catch {
        newImageIds = [];
      }

      if (newImageIds.length === 0) {
        alert("No se pudo generar nuevos ángulos. Intentá nuevamente.");
        return;
      }

      // Poll for images + preload signed URLs
      type PreloadedImage = {
        id: string;
        fileName: string;
        signedUrl: string;
        favorito: boolean;
      };
      const preloaded: PreloadedImage[] = [];
      const maxAttempts = 20;
      const delayMs = (ms: number) => new Promise((r) => setTimeout(r, ms));
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        for (const imgId of newImageIds) {
          if (preloaded.some((p) => p.id === imgId)) continue;
          try {
            const { data: imgData } = await supabase
              .from("imagenes_generadas")
              .select("file_name, favorito")
              .eq("id", imgId)
              .eq("id_company", companyId)
              .single();
            if (!imgData?.file_name) continue;
            const filePath = `${companyId}/full/${imgData.file_name}`;
            const { data: signedUrlData } = await supabase.storage
              .from("generadas")
              .createSignedUrl(filePath, 3600);
            if (!signedUrlData?.signedUrl) continue;
            preloaded.push({
              id: imgId,
              fileName: imgData.file_name,
              signedUrl: signedUrlData.signedUrl,
              favorito: imgData.favorito === true,
            });
            // Preload into browser cache
            const img = new Image();
            img.src = signedUrlData.signedUrl;
          } catch {}
        }
        if (preloaded.length === newImageIds.length) break;
        await delayMs(1500);
      }

      // Navigate to generated images page with preloaded data
      navigate("/redes-sociales/imagenes-generadas", {
        state: {
          imageIds: newImageIds,
          companyId,
          preloadedImages: preloaded,
          fromHistory: true,
          sourceImageId: targetImage.id,
        },
      });
    } catch (_e) {
      alert("No se pudo generar nuevos ángulos. Intentá nuevamente.");
    } finally {
      setShowCarruselLoading(false);
      carruselLockRef.current = false;
    }
  };

  // ─── Video generation menu (Animar) ───
  const [showVideoMenu, setShowVideoMenu] = useState<boolean>(false);
  const [videoMenuAnimated, setVideoMenuAnimated] = useState<boolean>(false);
  const [videoMenuDragY, setVideoMenuDragY] = useState<number>(0);
  const videoDragStartY = useRef<number | null>(null);
  const [videoStep, setVideoStep] = useState<"select" | "prompt">("select");
  const [videoPrompt, setVideoPrompt] = useState<string>("");
  const [videoDuration, setVideoDuration] = useState<number>(8);
  const [videoMultiScene, setVideoMultiScene] = useState<boolean>(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState<boolean>(false);
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] =
    useState<boolean>(false);
  const videoTargetImageRef = useRef<GeneratedImage | null>(null);
  const [loadingVideoPrompt, setLoadingVideoPrompt] = useState<boolean>(false);
  const [videoPromptError, setVideoPromptError] = useState<string | null>(null);
  const videoPromptFetchedRef = useRef(false);
  const videoImagesCost = videoMultiScene ? videoDuration * 2 : videoDuration;

  // ─── Upscale menu (Mejorar resolución) ───
  const [showUpscaleMenu, setShowUpscaleMenu] = useState<boolean>(false);
  const [upscaleMenuAnimated, setUpscaleMenuAnimated] =
    useState<boolean>(false);
  const [upscaleMenuDragY, setUpscaleMenuDragY] = useState<number>(0);
  const upscaleDragStartY = useRef<number | null>(null);
  const [upscaleScale, setUpscaleScale] = useState<2 | 4>(2);
  const [isUpscaling, setIsUpscaling] = useState<boolean>(false);
  const [upscaleProgressComplete, setUpscaleProgressComplete] =
    useState<boolean>(false);
  const upscaleTargetImageRef = useRef<GeneratedImage | null>(null);
  const upscaleImagesCost = upscaleScale === 2 ? 1 : 2;
  // Toggle "Original / Mejorada" — estado compartido entre vistas
  const [showingOriginalAfterUpscale, setShowingOriginalAfterUpscale] =
    useState<boolean>(false);

  const openVideoMenu = async (imageOverride?: GeneratedImage | null) => {
    const targetImage = imageOverride || currentViewImage || selectedImage;
    if (!targetImage) return;
    videoTargetImageRef.current = targetImage;
    // Check plan: only pro (negocio) and ultra (enterprise) can generate video
    try {
      const { data: companyProfile } = await supabase
        .from("company_profiles")
        .select("plan")
        .eq("id_company", companyId)
        .single();
      const plan = (companyProfile as any)?.plan;
      if (
        plan &&
        plan !== "negocio" &&
        plan !== "enterprise" &&
        plan !== "enterprise1" &&
        plan !== "enterprise2" &&
        plan !== "enterprise3" &&
        plan !== "enterprise_plus"
      ) {
        setShowVideoPlanRestriction(true);
        return;
      }
    } catch {}
    setShowMoreMenu(false);
    setVideoStep("select");
    setVideoPrompt("");
    setVideoDuration(8);
    setVideoMultiScene(false);
    setLoadingVideoPrompt(false);
    setVideoPromptError(null);
    videoPromptFetchedRef.current = false;
    setShowVideoMenu(true);
    requestAnimationFrame(() => setVideoMenuAnimated(true));
  };

  const closeVideoMenu = () => {
    setVideoMenuAnimated(false);
    setTimeout(() => {
      setShowVideoMenu(false);
      setVideoMenuDragY(0);
      setVideoStep("select");
      setVideoPrompt("");
      setVideoDuration(8);
      setVideoMultiScene(false);
      setLoadingVideoPrompt(false);
      setVideoPromptError(null);
      videoPromptFetchedRef.current = false;
    }, 300);
  };

  // Upscale menu helpers
  const openUpscaleMenu = async (imageOverride?: GeneratedImage | null) => {
    const targetImage = imageOverride || currentViewImage || selectedImage;
    if (!targetImage || isVideoItem(targetImage)) return;
    upscaleTargetImageRef.current = targetImage;
    // Check credits
    try {
      const { data: cpData } = await supabase
        .from("company_profiles")
        .select("plan, imagenes_restantes")
        .eq("id_company", companyId)
        .single();
      const remaining = (cpData as any)?.imagenes_restantes as
        | number
        | null
        | undefined;
      const cost = upscaleScale === 2 ? 1 : 2;
      if (typeof remaining === "number" && remaining < cost) {
        const isFree = (cpData as any)?.plan === "gratis";
        setSubscriptionLimitType(
          isFree ? "free-trial-ended" : "plan-limit-reached",
        );
        return;
      }
    } catch {}
    setShowMoreMenu(false);
    setUpscaleScale(2);
    setShowUpscaleMenu(true);
    requestAnimationFrame(() => setUpscaleMenuAnimated(true));
  };

  const closeUpscaleMenu = () => {
    setUpscaleMenuAnimated(false);
    setTimeout(() => {
      setShowUpscaleMenu(false);
      setUpscaleMenuDragY(0);
      setUpscaleScale(2);
    }, 300);
  };

  const handleUpscale = async () => {
    const targetImage = upscaleTargetImageRef.current;
    if (!targetImage || isUpscaling) return;
    setIsUpscaling(true);
    setUpscaleProgressComplete(false);
    closeUpscaleMenu();
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");

      const n8nBase = import.meta.env.DEV
        ? "/api/n8n"
        : "https://ai-agency-dt7d.onrender.com";
      const response = await fetch(`${n8nBase}/webhook/upscale_imagen`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          user_id: user.id,
          image_id: targetImage.id,
          scale: upscaleScale,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setUpscaleProgressComplete(true);
        // Obtener signed URL de la imagen mejorada y activar toggle
        try {
          const upscaledPath =
            data.upscaled_path ||
            `${companyId}/upscaled/${targetImage.file_name}`;
          const { data: upData } = await supabase.storage
            .from("generadas")
            .createSignedUrl(upscaledPath, 3600);
          if (upData?.signedUrl) {
            setShowingOriginalAfterUpscale(false);
            // Actualizar imagen con nuevo upscaled_path y upscaledSignedUrl (mantener signedUrl original)
            const updateImg = (img: GeneratedImage) =>
              img.id === targetImage.id
                ? {
                    ...img,
                    upscaled_path: upscaledPath,
                    upscaledSignedUrl: upData.signedUrl,
                  }
                : img;
            setSelectedImage((prev) => (prev ? updateImg(prev) : prev));
            setCurrentViewImage((prev) => (prev ? updateImg(prev) : prev));
            setAllImages((prev) => prev.map(updateImg));
            setFilteredImages((prev) => prev.map(updateImg));
            sileo.success({
              title: "Imagen mejorada",
              description:
                "Usá el toggle para comparar entre la original y la mejorada.",
            });
          }
        } catch (err) {
          console.error(
            "Error obteniendo signed URL de la imagen mejorada:",
            err,
          );
        }
      } else if (data.error === "insufficient_credits") {
        setUpscaleProgressComplete(true);
        const isFree = data.plan === "gratis";
        setSubscriptionLimitType(
          isFree ? "free-trial-ended" : "plan-limit-reached",
        );
      } else {
        sileo.error({
          title: "Error al mejorar la resolución",
          description:
            "No pudimos procesar la imagen. Intentá nuevamente en unos minutos.",
        });
        setUpscaleProgressComplete(true);
      }
    } catch (error) {
      console.error("Error upscaling image:", error);
      sileo.error({
        title: "Error al mejorar la resolución",
        description:
          "No pudimos procesar la imagen. Intentá nuevamente en unos minutos.",
      });
      setUpscaleProgressComplete(true);
    } finally {
      try {
        window.dispatchEvent(new Event("atlas:credits-updated"));
      } catch {}
      setIsUpscaling(false);
    }
  };

  const fetchVideoPrompt = async () => {
    if (videoPromptFetchedRef.current) return;
    videoPromptFetchedRef.current = true;
    const targetImage = videoTargetImageRef.current;
    if (!targetImage) return;
    try {
      setLoadingVideoPrompt(true);
      setVideoPromptError(null);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const response = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/generar_prompt_video",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({ user_id: user.id, image_id: targetImage.id }),
        },
      );
      if (!response.ok) throw new Error("Error al generar la descripción");
      const data = await response.json();
      setVideoPrompt(data?.response || data?.prompt || "");
    } catch (e) {
      console.error("Error fetching video prompt:", e);
      setVideoPromptError(
        e instanceof Error ? e.message : "Error al generar la descripción",
      );
    } finally {
      setLoadingVideoPrompt(false);
    }
  };

  const handleGoToPromptStep = () => {
    setVideoStep("prompt");
    fetchVideoPrompt();
  };

  const handleGenerateVideo = async () => {
    const targetImage = videoTargetImageRef.current;
    if (!targetImage || isGeneratingVideo) return;
    closeVideoMenu();
    setIsGeneratingVideo(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        alert("No se pudo identificar al usuario");
        return;
      }

      const requiredImages = videoImagesCost;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      const userCompanyId = profile?.id_company || companyId;
      if (userCompanyId) {
        const { data: companyProfile } = await supabase
          .from("company_profiles")
          .select("plan, imagenes_restantes")
          .eq("id_company", userCompanyId)
          .single();

        const remaining = (companyProfile as any)?.imagenes_restantes as
          | number
          | null
          | undefined;
        if (typeof remaining === "number" && remaining < requiredImages) {
          const isFree = (companyProfile as any)?.plan === "gratis";
          setSubscriptionLimitType(
            isFree ? "free-trial-ended" : "plan-limit-reached",
          );
          setShowSubscriptionLimitModal(true);
          return;
        }
      }

      const requestData: Record<string, any> = {
        user_id: user.id,
        tipo_video: "cinematografico",
        prompt: videoPrompt.trim() || "",
        duracion: videoDuration,
        Format: "9:16",
        style_prompt: "",
        multiples_escenas: videoMultiScene,
        origen: "animar imagen",
        scenes: [
          {
            index: 0,
            prompt: videoPrompt.trim() || "",
            start_frame: targetImage.id,
            end_frame: null,
            duration_seconds: videoDuration,
          },
        ],
      };

      const videoApiUrl = videoMultiScene
        ? "https://ai-agency-dt7d.onrender.com/webhook/video_cinema"
        : "https://ai-agency-dt7d.onrender.com/webhook/64e992a5-c15e-4744-aeb6-dfa530700b86";
      const response = await fetch(videoApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try {
        respText = await response.text();
      } catch {}

      if (!response.ok) {
        alert("No se pudo generar el video. Intentá nuevamente.");
        return;
      }

      let data: any = null;
      try {
        data = respText ? JSON.parse(respText) : null;
      } catch {}

      let videoId: string | null = null;
      if (
        data?.response === "success" &&
        (data?.video_id || data?.video_generado_id)
      ) {
        videoId = String(data.video_id || data.video_generado_id);
      }

      if (!videoId) {
        alert("No se pudo generar el video. Intentá nuevamente.");
        return;
      }

      // Poll Supabase for the video file
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
      let videoPreloaded: {
        id: string;
        fileName: string;
        signedUrl: string;
        favorito: boolean;
        isVideo: boolean;
      } | null = null;
      for (let attempt = 1; attempt <= 15; attempt++) {
        try {
          const { data: videoData } = await supabase
            .from("imagenes_generadas")
            .select("file_name, favorito, created_at, variante_id")
            .eq("id", videoId)
            .eq("id_company", companyId)
            .single();
          if (!videoData?.file_name) {
            await delay(3000);
            continue;
          }
          const filePath = `${companyId}/full/${videoData.file_name}`;
          const { data: signedUrlData } = await supabase.storage
            .from("generadas")
            .createSignedUrl(filePath, 3600);
          if (!signedUrlData?.signedUrl) {
            await delay(3000);
            continue;
          }
          videoPreloaded = {
            id: videoId,
            fileName: videoData.file_name,
            signedUrl: signedUrlData.signedUrl,
            favorito: videoData.favorito === true,
            isVideo: true,
          };
          break;
        } catch {
          await delay(3000);
        }
      }
      if (!videoPreloaded) {
        alert(
          "El video se generó pero aún no está listo. Recargá la página en unos minutos.",
        );
      } else {
        // Navigate to GeneratedImagesInline with the video
        navigate("/redes-sociales/imagenes-generadas", {
          state: {
            imageIds: [videoId],
            companyId,
            preloadedImages: [videoPreloaded],
            fromHistory: true,
            sourceImageId: targetImage.id,
          },
        });
      }
    } catch (_e) {
      alert("No se pudo generar el video. Intentá nuevamente.");
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  // State for download status overlay
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [showInAppBrowserModal, setShowInAppBrowserModal] = useState(false);

  // Resuelve la URL firmada completa de una imagen (usa cache + bucket si hace falta)
  const resolveFullSignedUrl = async (
    image: GeneratedImage,
  ): Promise<string | null> => {
    if (!image.file_name || !companyId) return null;
    const shouldUseUpscaled =
      !!image.upscaled_path && !!image.upscaledSignedUrl && !isVideoItem(image);
    if (shouldUseUpscaled) return image.upscaledSignedUrl!;
    if (image.signedUrl && image.signedUrl !== image.thumbnailUrl)
      return image.signedUrl;
    const fullFilePath = `${companyId}/full/${image.file_name}`
      .replace(/\/+/, "/")
      .replace(/\/+/g, "/");
    const cached = getCachedSignedUrl(fullFilePath);
    if (cached) return cached;
    const { data, error } = await supabase.storage
      .from("generadas")
      .createSignedUrl(fullFilePath, 3600);
    if (error || !data?.signedUrl) return null;
    setCachedSignedUrl(fullFilePath, data.signedUrl);
    return data.signedUrl;
  };

  // Descarga masiva: zip en desktop, descargas individuales secuenciales en mobile
  const handleBulkDownload = async () => {
    if (bulkDownloading || selectedIds.size === 0) return;
    if (isInAppBrowser()) {
      setShowInAppBrowserModal(true);
      return;
    }
    const ids = Array.from(selectedIds);
    const items = displayImages.filter((img) => ids.includes(img.id));
    if (items.length === 0) return;

    setBulkDownloading(true);
    setBulkError(null);
    setBulkProgress({ done: 0, total: items.length });

    const isMobile = isMobileViewport();

    // Cuento previamente cuántas imágenes hay por variante en este lote
    // para decidir si lleva sufijo `_N` o no. Si una variante aparece una
    // sola vez en la selección, queda con nombre limpio (chaqueta_beige.png);
    // si aparece varias, se enumeran reiniciando el contador por variante.
    const variantTotals = new Map<string, number>();
    for (const img of items) {
      const key = slugifyVariantName(img.variante_nombre);
      variantTotals.set(key, (variantTotals.get(key) || 0) + 1);
    }
    const variantCounters = new Map<string, number>();
    const nameForItem = (img: GeneratedImage, blobMime?: string): string => {
      const key = slugifyVariantName(img.variante_nombre);
      const total = variantTotals.get(key) || 1;
      const next = (variantCounters.get(key) || 0) + 1;
      variantCounters.set(key, next);
      return buildDownloadFileName({
        variantName: img.variante_nombre,
        index: total > 1 ? next : undefined,
        mime: blobMime,
        originalFileName: img.file_name,
        forceVideo: isVideoItem(img),
      });
    };

    try {
      if (isMobile) {
        // MOBILE: descargas individuales secuenciales (mismo método que single)
        let done = 0;
        for (const img of items) {
          try {
            const url = await resolveFullSignedUrl(img);
            if (!url) throw new Error("No URL");
            const res = await fetch(url);
            if (!res.ok) throw new Error("Fetch failed");
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = nameForItem(img, blob.type);
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(blobUrl);
            }, 200);
            // Delay entre descargas para que el browser no las bloquee
            await new Promise((r) => setTimeout(r, 400));
          } catch (err) {
            console.warn("Bulk single download failed:", img.id, err);
          }
          done++;
          setBulkProgress({ done, total: items.length });
        }
      } else {
        // DESKTOP: armar un ZIP único con fflate
        const { zip } = await import("fflate");
        const files: Record<string, Uint8Array> = {};
        let done = 0;
        for (const img of items) {
          try {
            const url = await resolveFullSignedUrl(img);
            if (!url) throw new Error("No URL");
            const res = await fetch(url);
            if (!res.ok) throw new Error("Fetch failed");
            const buf = new Uint8Array(await res.arrayBuffer());
            let name = nameForItem(img, res.headers.get("content-type") || undefined);
            // Defensa anti-colisiones: si por algún motivo terminamos con
            // dos nombres iguales (variante distinta que slugifica igual,
            // por ejemplo), añadimos el id como sufijo antes de la extensión.
            if (files[name]) {
              const dot = name.lastIndexOf(".");
              const base = dot >= 0 ? name.slice(0, dot) : name;
              const ext = dot >= 0 ? name.slice(dot) : "";
              name = `${base}_${img.id}${ext}`;
            }
            files[name] = buf;
          } catch (err) {
            console.warn("Bulk zip fetch failed:", img.id, err);
          }
          done++;
          setBulkProgress({ done, total: items.length });
        }

        if (Object.keys(files).length === 0) {
          throw new Error("No se pudo descargar ninguna imagen");
        }

        const zipped: Uint8Array = await new Promise((resolve, reject) => {
          zip(files, (err, data) => {
            if (err) reject(err);
            else resolve(data);
          });
        });

        const today = new Date().toISOString().slice(0, 10);
        const zipBlob = new Blob([zipped as any], { type: "application/zip" });
        const blobUrl = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `atlas-${today}.zip`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        }, 200);
      }

      // Marcar como descargadas en DB
      try {
        if (companyId) {
          await supabase
            .from("imagenes_generadas")
            .update({ descargada: true })
            .in("id", ids)
            .eq("id_company", companyId);
        }
      } catch {}

      exitSelectionMode();
    } catch (err: any) {
      console.error("Bulk download error:", err);
      setBulkError(err?.message || "Error al descargar");
    } finally {
      setBulkDownloading(false);
      setBulkProgress({ done: 0, total: 0 });
    }
  };

  const handleDownload = async (
    image: GeneratedImage | null,
    e?: React.MouseEvent,
  ) => {
    if (!image || !image.file_name || isDownloading) return;
    if (isInAppBrowser()) {
      setShowInAppBrowserModal(true);
      return;
    }
    try {
      // Prevent the default action to avoid navigation issues
      e?.preventDefault?.();

      // Show downloading status
      setIsDownloading(true);
      setDownloadSuccess(false);

      // Determine which version to download based on current toggle state
      const shouldDownloadUpscaled =
        !!image.upscaled_path &&
        !!image.upscaledSignedUrl &&
        !showingOriginalAfterUpscale &&
        !isVideoItem(image);

      // Ensure we have the full signed URL before downloading
      let downloadUrl = shouldDownloadUpscaled
        ? image.upscaledSignedUrl!
        : image.signedUrl;
      if (
        !shouldDownloadUpscaled &&
        (!downloadUrl || downloadUrl === image.thumbnailUrl)
      ) {
        const fullFilePath = `${companyId}/full/${image.file_name}`
          .replace(/\/+/, "/")
          .replace(/\/+/g, "/");
        const cached = getCachedSignedUrl(fullFilePath);
        if (cached) {
          downloadUrl = cached;
        } else {
          const { data, error } = await supabase.storage
            .from("generadas")
            .createSignedUrl(fullFilePath, 3600);
          if (!error && data?.signedUrl) {
            downloadUrl = data.signedUrl;
            setCachedSignedUrl(fullFilePath, downloadUrl);
            // Also update state so lightbox uses the full URL
            const update = (img: GeneratedImage) =>
              img.id === image.id ? { ...img, signedUrl: downloadUrl } : img;
            setAllImages((prev) => prev.map(update));
            setFilteredImages((prev) => prev.map(update));
            setSelectedImage((prev) =>
              prev?.id === image.id
                ? { ...prev, signedUrl: downloadUrl }
                : prev,
            );
            setCurrentViewImage((prev) =>
              prev?.id === image.id
                ? { ...prev, signedUrl: downloadUrl }
                : prev,
            );
          }
        }
      }

      if (!downloadUrl) {
        throw new Error("No download URL available");
      }

      const isVideo = isVideoItem(image);

      // Helper: on iOS use native share sheet (to save to Camera Roll), on Android/desktop use <a> download
      const shareOrDownloadBlob = async (blob: Blob, fileName: string) => {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isIOS && navigator.share && navigator.canShare) {
          // Try original MIME type first
          const file = new File([blob], fileName, { type: blob.type });
          if (navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({ files: [file] });
              return;
            } catch (shareErr: any) {
              if (shareErr?.name === "AbortError") return;
              console.warn("Share failed with original MIME:", shareErr);
            }
          }
          // For videos: try video/quicktime MIME if video/mp4 was rejected
          if (
            blob.type.startsWith("video/") &&
            blob.type !== "video/quicktime"
          ) {
            const movBlob = new Blob([blob], { type: "video/quicktime" });
            const movName = fileName.replace(/\.\w+$/, ".mov");
            const movFile = new File([movBlob], movName, {
              type: "video/quicktime",
            });
            if (navigator.canShare({ files: [movFile] })) {
              try {
                await navigator.share({ files: [movFile] });
                return;
              } catch (shareErr: any) {
                if (shareErr?.name === "AbortError") return;
                console.warn("Share failed with quicktime MIME:", shareErr);
              }
            }
          }
        }
        // Fallback: classic <a> download
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = "none";
        document.body.appendChild(link);
        await new Promise<void>((resolve) =>
          setTimeout(() => {
            link.click();
            resolve();
          }, 50),
        );
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);
      };

      // Mark as downloaded in DB (shared helper)
      const markAsDownloaded = async () => {
        try {
          if (companyId) {
            await supabase
              .from("imagenes_generadas")
              .update({ descargada: true })
              .eq("id", image.id)
              .eq("id_company", companyId);
          }
        } catch {}
      };

      // Nombre base para el filename de descarga: usamos el nombre de la
      // variante (slugificado) en vez del file_name crudo del Storage.
      // Para versiones upscaleadas añadimos `_upscaled` al slug. Single
      // download => sin índice (chaqueta_beige.png / chaqueta_beige_upscaled.png).
      const downloadVariantName = shouldDownloadUpscaled
        ? `${image.variante_nombre || "imagen"}_upscaled`
        : image.variante_nombre;

      // For videos, skip enhancement and download directly
      if (isVideo) {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const videoFileName = buildDownloadFileName({
          variantName: downloadVariantName,
          originalFileName: image.file_name,
          forceVideo: true,
        });

        if (isIOS && navigator.share && navigator.canShare) {
          // Check if blob is already fully resolved in memory (prefetched earlier)
          const resolvedBlob = videoResolvedBlobRef.current.get(image.id);

          if (resolvedBlob) {
            // Blob ready → share instantly within user gesture (no awaits that break gesture context)
            const videoBlob = new Blob([resolvedBlob], { type: "video/mp4" });
            const file = new File([videoBlob], videoFileName, {
              type: "video/mp4",
            });
            if (navigator.canShare({ files: [file] })) {
              try {
                await navigator.share({ files: [file] });
                await markAsDownloaded();
                setDownloadSuccess(true);
                setTimeout(() => {
                  setIsDownloading(false);
                  setDownloadSuccess(false);
                }, 3000);
                return;
              } catch (shareErr: any) {
                if (shareErr?.name === "AbortError") {
                  setIsDownloading(false);
                  return;
                }
              }
            }
            // Fallback: try quicktime MIME
            const movBlob = new Blob([resolvedBlob], {
              type: "video/quicktime",
            });
            const movFile = new File(
              [movBlob],
              videoFileName.replace(/\.mp4$/, ".mov"),
              { type: "video/quicktime" },
            );
            if (navigator.canShare({ files: [movFile] })) {
              try {
                await navigator.share({ files: [movFile] });
                await markAsDownloaded();
                setDownloadSuccess(true);
                setTimeout(() => {
                  setIsDownloading(false);
                  setDownloadSuccess(false);
                }, 3000);
                return;
              } catch (shareErr: any) {
                if (shareErr?.name === "AbortError") {
                  setIsDownloading(false);
                  return;
                }
              }
            }
          } else {
            // Blob NOT ready yet → wait for prefetch then try share directly
            let blobPromise = videoBlobCacheRef.current.get(image.id);
            if (!blobPromise) {
              blobPromise = new Promise<Blob>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("GET", downloadUrl, true);
                xhr.responseType = "blob";
                xhr.onload = () => {
                  if (xhr.status >= 200 && xhr.status < 300) {
                    const blob = xhr.response as Blob;
                    videoResolvedBlobRef.current.set(image.id, blob);
                    resolve(blob);
                  } else {
                    reject(new Error(`XHR failed: ${xhr.status}`));
                  }
                };
                xhr.onerror = () => reject(new Error("XHR network error"));
                xhr.send();
              });
              videoBlobCacheRef.current.set(image.id, blobPromise);
            }
            const rawBlob = await blobPromise;
            // Try share directly — Safari may still honor the gesture if await was short
            const videoBlob = new Blob([rawBlob], { type: "video/mp4" });
            const file = new File([videoBlob], videoFileName, {
              type: "video/mp4",
            });
            let shared = false;
            if (navigator.canShare?.({ files: [file] })) {
              try {
                await navigator.share({ files: [file] });
                shared = true;
              } catch (shareErr: any) {
                if (shareErr?.name === "AbortError") {
                  setIsDownloading(false);
                  return;
                }
                // NotAllowedError = gesture expired → fall through to overlay
              }
            }
            if (!shared) {
              // Try quicktime before falling back to overlay
              const movBlob = new Blob([rawBlob], { type: "video/quicktime" });
              const movFile = new File(
                [movBlob],
                videoFileName.replace(/\.mp4$/, ".mov"),
                { type: "video/quicktime" },
              );
              if (navigator.canShare?.({ files: [movFile] })) {
                try {
                  await navigator.share({ files: [movFile] });
                  shared = true;
                } catch (shareErr: any) {
                  if (shareErr?.name === "AbortError") {
                    setIsDownloading(false);
                    return;
                  }
                }
              }
            }
            if (shared) {
              await markAsDownloaded();
              setDownloadSuccess(true);
              setTimeout(() => {
                setIsDownloading(false);
                setDownloadSuccess(false);
              }, 3000);
              return;
            }
            // Gesture expired — show "Save Video" button for fresh tap
            setPendingVideoShare({
              imageId: image.id,
              fileName: videoFileName,
            });
            await markAsDownloaded();
            return;
          }
        }

        // Non-iOS or fallback: fetch + <a> download
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error("Failed to fetch video");

        const originalBlob = await response.blob();
        const blob = new Blob([originalBlob], { type: "video/mp4" });

        await shareOrDownloadBlob(blob, videoFileName);
        await markAsDownloaded();

        setDownloadSuccess(true);
        setTimeout(() => {
          setIsDownloading(false);
          setDownloadSuccess(false);
        }, 3000);
        return;
      }

      // Get the user ID for the API request
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // First try to enhance the image via the enhancement API
      try {
        const enhanceResponse = await fetch(
          "https://ai-agency-dt7d.onrender.com/webhook/enhance",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: user.id,
              image_file_name: image.file_name,
            }),
          },
        );

        if (enhanceResponse.ok) {
          const enhanceData = await enhanceResponse.json();
          if (enhanceData && enhanceData.url) {
            const enhancedResponse = await fetch(enhanceData.url);
            if (enhancedResponse.ok) {
              const blob = await enhancedResponse.blob();

              await shareOrDownloadBlob(
                blob,
                buildDownloadFileName({
                  variantName: downloadVariantName,
                  mime: blob.type,
                  originalFileName: image.file_name,
                }),
              );
              await markAsDownloaded();

              setDownloadSuccess(true);
              setTimeout(() => {
                setIsDownloading(false);
                setDownloadSuccess(false);
              }, 3000);

              return;
            }
          }
        }

        throw new Error("Enhancement failed or returned invalid data");
      } catch (enhanceErr) {
        console.error(
          "Enhancement failed, falling back to direct download:",
          enhanceErr,
        );

        const response = await fetch(downloadUrl, {
          cache: "force-cache",
        });

        if (!response.ok) throw new Error("Failed to fetch image");

        const blob = await response.blob();

        await shareOrDownloadBlob(
          blob,
          buildDownloadFileName({
            variantName: downloadVariantName,
            mime: blob.type,
            originalFileName: image.file_name,
          }),
        );
        await markAsDownloaded();

        setDownloadSuccess(true);
        setTimeout(() => {
          setIsDownloading(false);
          setDownloadSuccess(false);
        }, 3000);
      }
    } catch (err) {
      console.error("Error downloading image:", err);
      alert("Error downloading image. Please try again.");
      setIsDownloading(false);
    }
  };

  // Show deletion confirmation UI
  const showDeleteConfirmation = (
    image: GeneratedImage | null,
    e?: React.MouseEvent,
  ) => {
    if (!image) return;
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setImageToDelete(image);
    setShowDeleteConfirm(true);
  };

  // Cancel delete operation
  const cancelDelete = (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowDeleteConfirm(false);
    setImageToDelete(null);
  };

  // Delete image from Supabase storage and database
  const handleDeleteImage = async (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!imageToDelete) return;

    try {
      setDeleting(true); // Show deleting state

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!profile?.id_company)
        throw new Error("No company associated with user");

      // Resolve id_company (UUID) to build storage path
      let idCompany = companyUuid || companyId || profile.id_company;
      if (!idCompany) throw new Error("No company UUID to build storage path");

      // Define the file paths for both full and optimized versions in 'generadas' bucket
      const fullFilePath = `${idCompany}/full/${imageToDelete.file_name}`;
      const optFilePath = `${idCompany}/opt/${imageToDelete.file_name}`;

      // Delete the image files from storage
      const { error: fullDeleteError } = await supabase.storage
        .from("generadas")
        .remove([fullFilePath]);

      if (fullDeleteError) {
        console.error("Error deleting full-size image:", fullDeleteError);
      }

      // Try to delete the optimized version too (if it exists)
      const { error: optDeleteError } = await supabase.storage
        .from("generadas")
        .remove([optFilePath]);

      if (optDeleteError) {
        console.error("Error deleting optimized image:", optDeleteError);
        // Continue even if optimized version couldn't be deleted
      }

      // Update the database record instead of deleting it
      // Set file_name to null and active to false
      const { error: dbUpdateError } = await supabase
        .from("imagenes_generadas")
        .update({
          file_name: null,
          active: false,
        })
        .eq("id", imageToDelete.id)
        .eq("id_company", profile.id_company);

      if (dbUpdateError) {
        throw dbUpdateError;
      }

      // Save current image position to navigate to next image after deletion
      const currentIndex = filteredImages.findIndex(
        (img) => img.id === imageToDelete.id,
      );
      const nextIndex =
        currentIndex + 1 >= filteredImages.length ? 0 : currentIndex + 1;
      const nextImage =
        filteredImages.length > 1 ? filteredImages[nextIndex] : null;

      // Update the UI
      setAllImages((prevImages) =>
        prevImages.filter((img) => img.id !== imageToDelete.id),
      );
      setFilteredImages((prevImages) =>
        prevImages.filter((img) => img.id !== imageToDelete.id),
      );

      // Reset delete confirmation state
      setShowDeleteConfirm(false);
      setImageToDelete(null);

      // Navigate to next image if one exists, otherwise close the modal
      if (selectedImage && selectedImage.id === imageToDelete.id) {
        if (nextImage) {
          // Navigate to the next image
          setSelectedImage(nextImage);
          setZoomLevel(1);
          setDragOffset({ x: 0, y: 0 });
        } else {
          // No more images, close the modal
          closeLightbox();
        }
      }
      // Handle mobile detail view: update currentViewImage or go back to grid
      if (currentViewImage && currentViewImage.id === imageToDelete.id) {
        if (nextImage) {
          setCurrentViewImage(nextImage);
        } else {
          setMobileView("grid");
          setCurrentViewImage(null);
        }
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      alert("Error al eliminar la imagen. Por favor, intenta de nuevo.");
    } finally {
      setDeleting(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setDragOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // Mostrar toggle siempre que la imagen activa tenga versión upscaleada disponible
  const activeToggleImage = selectedImage || currentViewImage;
  const showUpscaleToggle =
    activeToggleImage != null &&
    !!activeToggleImage.upscaled_path &&
    !!activeToggleImage.upscaledSignedUrl &&
    !!activeToggleImage.signedUrl &&
    !isVideoItem(activeToggleImage);

  // Toggle pill "Mejorada / Original" con slider animado
  const UpscaleComparisonToggle = ({
    variant,
  }: {
    variant: "desktop" | "mobile";
  }) => (
    <div
      className={
        variant === "desktop" ? "absolute top-3 left-3 z-20" : "mx-auto"
      }
    >
      <div
        className={`relative inline-flex items-center rounded-full p-0.5 select-none ${
          variant === "desktop"
            ? "bg-black/50 backdrop-blur-md border border-white/10"
            : "bg-neutral-200"
        }`}
      >
        <div
          className={`absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-full transition-transform duration-200 ease-out ${
            variant === "desktop" ? "bg-white" : "bg-white shadow-sm"
          }`}
          style={{
            transform: showingOriginalAfterUpscale
              ? "translateX(calc(100% + 4px))"
              : "translateX(0)",
          }}
        />
        <button
          type="button"
          onClick={() => setShowingOriginalAfterUpscale(false)}
          className={`relative z-10 px-3 py-1 text-[13px] font-medium rounded-full transition-colors ${
            !showingOriginalAfterUpscale
              ? "text-brand-black"
              : variant === "desktop"
                ? "text-white/90"
                : "text-brand-black/60"
          }`}
        >
          Mejorada
        </button>
        <button
          type="button"
          onClick={() => setShowingOriginalAfterUpscale(true)}
          className={`relative z-10 px-3 py-1 text-[13px] font-medium rounded-full transition-colors ${
            showingOriginalAfterUpscale
              ? "text-brand-black"
              : variant === "desktop"
                ? "text-white/90"
                : "text-brand-black/60"
          }`}
        >
          Original
        </button>
      </div>
    </div>
  );

  // No longer need client-side applyFiltersToImages, as filtering is now server-side

  // Client-side filter function removed; filtering is handled server-side now

  useEffect(() => {
    if (selectedImage) {
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("mouseleave", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseleave", handleMouseUp);
    };
  }, [selectedImage, isDragging]);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    // Only set up the observer if there are more images to load
    if (!hasMoreImages || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // When the observer element is visible, load more images
          fetchImages(true);
        }
      },
      { threshold: 0.5 }, // Trigger when 50% of the element is visible
    );

    // Observe the load more trigger element if it exists
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
      observer.disconnect();
    };
  }, [hasMoreImages, loadingMore, loading, filteredImages]);

  // Close custom dropdowns on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        timeframeDropdownRef.current &&
        !timeframeDropdownRef.current.contains(e.target as Node)
      )
        setTimeframeDropdownOpen(false);
      if (
        variantDropdownRef.current &&
        !variantDropdownRef.current.contains(e.target as Node)
      )
        setVariantDropdownOpen(false);
      if (
        brandDropdownRef.current &&
        !brandDropdownRef.current.contains(e.target as Node)
      )
        setBrandDropdownOpen(false);
      if (
        contentTypeDropdownRef.current &&
        !contentTypeDropdownRef.current.contains(e.target as Node)
      )
        setContentTypeDropdownOpen(false);
      const insideMobile = moreMenuMobileRef.current?.contains(
        e.target as Node,
      );
      const insideDesktop = moreMenuDesktopRef.current?.contains(
        e.target as Node,
      );
      if (!insideMobile && !insideDesktop) setShowMoreMenu(false);
      if (
        mobileDetailMoreRef.current &&
        !mobileDetailMoreRef.current.contains(e.target as Node)
      )
        setShowMobileDetailMore(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Drag-to-select: cuando estás en modo selección, arrastrar el dedo selecciona
  // todas las cards que toca. Usa elementFromPoint para encontrar la card bajo el dedo.
  useEffect(() => {
    if (!selectionMode) return;

    const applyToCard = (
      cardId: string | number,
      toggleTo: "select" | "deselect",
    ) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (toggleTo === "select") {
          if (next.has(cardId)) return prev;
          const limit = isMobileViewport()
            ? SELECTION_LIMIT_MOBILE
            : SELECTION_LIMIT_DESKTOP;
          if (next.size >= limit) {
            setBulkError(
              `Máximo ${limit} elementos${isMobileViewport() ? " en mobile" : ""}`,
            );
            return prev;
          }
          next.add(cardId);
        } else {
          if (!next.has(cardId)) return prev;
          next.delete(cardId);
        }
        return next;
      });
    };

    const onMove = (e: TouchEvent) => {
      const state = dragSelectRef.current;
      if (!state || e.touches.length === 0) return;
      const touch = e.touches[0];
      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      const dist = Math.hypot(dx, dy);

      // Activar drag-select una vez que se mueve lo suficiente.
      // 18px de threshold para no confundir con scroll incidental.
      if (!state.active && dist > 18) {
        state.active = true;
        state.hasMoved = true;
        // Aplicar el toggle a la card inicial (todavía no se había aplicado)
        const initialId = state.visited.values().next().value as
          | string
          | undefined;
        if (initialId) applyToCard(initialId, state.toggleTo);
      }

      if (state.active) {
        // Prevenir scroll mientras se arrastra
        if (e.cancelable) e.preventDefault();
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        const card = (el as Element | null)?.closest("[data-image-id]");
        if (card) {
          const cardId = card.getAttribute("data-image-id");
          if (cardId && !state.visited.has(cardId)) {
            state.visited.add(cardId);
            applyToCard(cardId, state.toggleTo);
          }
        }
      }
    };

    const onEnd = () => {
      const state = dragSelectRef.current;
      if (state?.hasMoved) {
        // Marcar para que el onClick subsiguiente NO toggle de nuevo la card inicial
        longPressTriggeredRef.current = true;
      }
      dragSelectRef.current = null;
    };

    // Mouse equivalents para desktop / DevTools sin touch emulation
    const onMouseMove = (e: MouseEvent) => {
      const state = dragSelectRef.current;
      if (!state) {
        return;
      }
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      const dist = Math.hypot(dx, dy);
      if (!state.active && dist > 8) {
        state.active = true;
        state.hasMoved = true;
        const initialId = state.visited.values().next().value as
          | string
          | undefined;
        if (initialId) applyToCard(initialId, state.toggleTo);
      }
      if (state.active) {
        e.preventDefault();
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const card = (el as Element | null)?.closest("[data-image-id]");
        if (card) {
          const cardId = card.getAttribute("data-image-id");
          if (cardId && !state.visited.has(cardId)) {
            state.visited.add(cardId);
            applyToCard(cardId, state.toggleTo);
          }
        }
      }
    };
    const onMouseUp = () => {
      const state = dragSelectRef.current;
      if (state?.hasMoved) {
        longPressTriggeredRef.current = true;
      }
      dragSelectRef.current = null;
    };

    // passive: false → permite e.preventDefault() para frenar el scroll durante drag
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("touchmove", onMove as any);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [selectionMode]);

  // Reset Instagram post success when closing the Instagram preview
  useEffect(() => {
    if (!showInstagramPreview) {
      setInstagramPostSuccess(false);
    }
  }, [showInstagramPreview]);

  useEffect(() => {
    // Prevent background scrolling when an image is selected
    if (selectedImage) {
      document.body.style.overflow = "hidden";

      // Add keyboard event listener for arrow navigation and escape key
      const handleKeyDown = (e: KeyboardEvent) => {
        // Handle Escape key to close the fullscreen view
        if (e.key === "Escape") {
          closeLightbox();
          return;
        }

        if (filteredImages.length <= 1) return;

        const currentIndex = filteredImages.findIndex(
          (img) => selectedImage && img.id === selectedImage.id,
        );
        console.log(
          "Keyboard navigation - currentIndex:",
          currentIndex,
          "selectedImage.id:",
          selectedImage?.id,
        );

        // Handle left arrow key - previous image
        if (e.key === "ArrowLeft") {
          const prevIndex =
            currentIndex <= 0 ? filteredImages.length - 1 : currentIndex - 1;
          const prevImage = filteredImages[prevIndex];
          if (prevImage) {
            setSelectedImage(prevImage);
            setZoomLevel(1);
            setDragOffset({ x: 0, y: 0 });
          }
        }

        // Handle right arrow key - next image
        if (e.key === "ArrowRight") {
          const nextIndex =
            currentIndex === -1 || currentIndex + 1 >= filteredImages.length
              ? 0
              : currentIndex + 1;
          const nextImage = filteredImages[nextIndex];
          if (nextImage) {
            setSelectedImage(nextImage);
            setZoomLevel(1);
            setDragOffset({ x: 0, y: 0 });
          }
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.overflow = "auto";
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
    return undefined;
  }, [selectedImage, isDragging, filteredImages]);

  // Unified Instagram connection prompt overlay (portal)
  const connectPrompt =
    showConnectPrompt && !isInstagramConnected
      ? createPortal(
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
            style={{ position: "fixed" }}
          >
            <div className="bg-surface-card rounded-lg shadow-md max-w-md w-full p-6 relative">
              <button
                onClick={() => setShowConnectPrompt(false)}
                className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-600 transition-colors rounded-sm"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-neutral-100 rounded-md flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-neutral-600" />
                </div>
              </div>
              <h3 className="text-[17px] font-semibold text-neutral-900 text-center mb-2">
                Conexión de Instagram requerida
              </h3>
              <p className="text-[length:var(--text-body)] text-neutral-600 text-center mb-6">
                Para poder publicar el contenido generado, primero debes
                conectar la cuenta de Instagram de la empresa a Atlas.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setShowConnectPrompt(false)}
                  className="h-[var(--btn-h)] px-5 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    navigate("/configuracion", {
                      state: { section: "instagram" },
                    });
                  }}
                  className="h-[var(--btn-h)] px-5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors flex items-center justify-center gap-2"
                >
                  <Instagram className="w-4 h-4" />
                  Conectar Instagram
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  // Client-side search filter
  const getDisplayImages = () => {
    let result = filteredImages;
    // Content type filter
    if (contentTypeFilter === "images") {
      result = result.filter((img) => !isVideoItem(img));
    } else if (contentTypeFilter === "videos") {
      result = result.filter((img) => isVideoItem(img));
    }
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (img) =>
          img.variante_nombre?.toLowerCase().includes(q) ||
          formatSmartDate(img.created_at).toLowerCase().includes(q),
      );
    }
    return result;
  };
  const displayImages = getDisplayImages();
  // Keep a ref so scroll/tap handlers can use the current display list
  const displayImagesRef = useRef<GeneratedImage[]>(displayImages);
  displayImagesRef.current = displayImages;

  return (
    <div className="pt-1 px-4 sm:p-8 min-h-[100dvh] md:min-h-screen bg-[#F2F2F7] sm:bg-transparent pb-0 relative max-w-6xl mx-auto">
      {/* Selection floating pill — abajo centrado, estilo iOS Photos */}
      {selectionMode && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[80] pointer-events-none"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
          }}
        >
          {bulkError && (
            <div className="pointer-events-auto mb-2 mx-auto w-fit px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-[12px] font-medium text-red-700 shadow-sm">
              {bulkError}
            </div>
          )}
          <div className="pointer-events-auto inline-flex items-center gap-1 bg-brand-black backdrop-blur-md rounded-full shadow-[0_10px_40px_rgba(0,0,0,0.4)] pl-2 pr-2 py-1.5 animate-fade-in">
            <button
              type="button"
              onClick={exitSelectionMode}
              disabled={bulkDownloading}
              aria-label="Cancelar selección"
              className="w-9 h-9 inline-flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/15 rounded-full transition-colors disabled:opacity-40"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="px-2 text-[13px] font-medium text-white whitespace-nowrap min-w-[120px] text-center tabular-nums">
              {bulkDownloading ? (
                `${bulkProgress.done}/${bulkProgress.total} descargando…`
              ) : selectedIds.size === 0 ? (
                <>
                  <span className="sm:hidden">Tocá una imagen</span>
                  <span className="hidden sm:inline">
                    Tocá una imagen para seleccionarla
                  </span>
                </>
              ) : (
                `${selectedIds.size} seleccionado${selectedIds.size === 1 ? "" : "s"}`
              )}
            </div>
            <button
              type="button"
              onClick={handleBulkDownload}
              disabled={selectedIds.size === 0 || bulkDownloading}
              className="h-9 px-4 inline-flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {bulkDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Descargar
            </button>
          </div>
        </div>
      )}

      {/* Download Status Overlay */}
      {(isDownloading || downloadSuccess) && !pendingVideoShare && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center z-[60] px-4 sm:px-0">
          <div className="bg-surface-card rounded-lg shadow-lg px-6 py-10 max-w-sm w-full mx-4 sm:mx-auto flex flex-col items-center">
            {isDownloading && !downloadSuccess && (
              <>
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-primary mb-4"></div>
                <h3 className="text-[17px] font-semibold text-brand-black mb-1">
                  Preparando descarga
                </h3>
              </>
            )}
            {downloadSuccess && (
              <>
                <div className="w-24 h-24 mb-2">
                  <DotLottieReact
                    src="/images/lottie_animations/Success.lottie"
                    autoplay
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
                <h3 className="text-[17px] font-semibold text-brand-black mb-1">
                  Imagen descargada
                </h3>
              </>
            )}
          </div>
        </div>
      )}
      {/* iOS Video Share overlay — blob loaded, needs fresh user gesture to trigger share sheet */}
      {pendingVideoShare && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center z-[60] px-4 sm:px-0">
          <div className="bg-surface-card rounded-lg shadow-lg px-6 py-8 max-w-sm w-full mx-4 sm:mx-auto flex flex-col items-center">
            <Download className="w-12 h-12 text-brand-primary mb-4" />
            <h3 className="text-[17px] font-semibold text-brand-black mb-2">
              Video listo
            </h3>
            <p className="text-[13px] text-brand-black/60 text-center mb-5">
              Toca el botón para guardar el video
            </p>
            <button
              className="w-full bg-brand-primary text-white text-[15px] font-semibold py-3 rounded-md active:opacity-80 transition-opacity"
              onClick={async () => {
                const blob = videoResolvedBlobRef.current.get(
                  pendingVideoShare.imageId,
                );
                if (!blob) {
                  setPendingVideoShare(null);
                  setIsDownloading(false);
                  return;
                }
                const videoBlob = new Blob([blob], { type: "video/mp4" });
                const file = new File([videoBlob], pendingVideoShare.fileName, {
                  type: "video/mp4",
                });
                try {
                  if (navigator.canShare?.({ files: [file] })) {
                    await navigator.share({ files: [file] });
                  } else {
                    const movBlob = new Blob([blob], {
                      type: "video/quicktime",
                    });
                    const movFile = new File(
                      [movBlob],
                      pendingVideoShare.fileName.replace(/\.mp4$/, ".mov"),
                      { type: "video/quicktime" },
                    );
                    if (navigator.canShare?.({ files: [movFile] })) {
                      await navigator.share({ files: [movFile] });
                    }
                  }
                } catch (err: any) {
                  if (err?.name === "AbortError") {
                    /* user cancelled */
                  }
                }
                setPendingVideoShare(null);
                setIsDownloading(false);
                setDownloadSuccess(false);
              }}
            >
              Guardar Video
            </button>
            <button
              className="mt-3 text-[13px] text-brand-black/50 font-medium active:opacity-60"
              onClick={() => {
                setPendingVideoShare(null);
                setIsDownloading(false);
                setDownloadSuccess(false);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      <div className="mb-6">
        {/* Mobile header — search bar + filter button */}
        <div className="sm:hidden mb-4 mt-2 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Buscar por producto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-neutral-200 rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              />
            </div>
            <button
              onClick={() => {
                setMobileSelectedTimeframe(selectedTimeframe);
                setMobileSelectedVariant(selectedVariant);
                setMobileOnlyFavorites(onlyFavorites);
                setMobileSelectedBrandId(selectedBrandId);
                setShowMobileFilters(true);
                setTimeout(() => setFilterMenuAnimated(true), 10);
              }}
              className="h-[var(--btn-h)] px-5 inline-flex items-center gap-2 text-[length:var(--text-body)] font-medium bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md shadow-sm transition-colors shrink-0"
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
            {/* Seleccionar (mobile) — alternativa al long-press */}
            <button
              type="button"
              onClick={() => setSelectionMode((prev) => !prev)}
              className={`h-[var(--btn-h)] px-4 inline-flex items-center gap-2 text-[length:var(--text-body)] font-medium border rounded-md shadow-sm transition-colors shrink-0 ${
                selectionMode
                  ? "bg-brand-primary hover:bg-brand-primary-hover text-white border-brand-primary"
                  : "bg-surface-card text-neutral-900 border-neutral-200 hover:bg-neutral-100"
              }`}
              aria-label="Seleccionar"
            >
              <GalleryVerticalEnd className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-center justify-between mb-5 h-11">
          <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4">
            Historial de imágenes generadas
          </h1>
        </div>

        {/* Desktop Filters - hidden on mobile */}
        <div className="hidden md:flex items-center gap-3 mb-4">
          {/* Search bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Buscar por producto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 border border-neutral-200 rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
            />
          </div>
          {/* Timeframe — custom dropdown */}
          <div ref={timeframeDropdownRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setTimeframeDropdownOpen(!timeframeDropdownOpen);
                setVariantDropdownOpen(false);
              }}
              className="h-[var(--btn-h)] px-4 inline-flex items-center justify-between gap-2 border border-neutral-200 rounded-md bg-surface-card hover:bg-surface-elevated text-[length:var(--text-body)] font-medium text-neutral-900 shadow-sm outline-none transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>
                {
                  {
                    all: "Todas",
                    today: "Hoy",
                    last7days: "Últimos 7 días",
                    last30days: "Últimos 30 días",
                    last90days: "Últimos 90 días",
                  }[selectedTimeframe]
                }
              </span>
              <ChevronDown
                className={`w-4 h-4 text-neutral-400 transition-transform ${timeframeDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>
            {timeframeDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full min-w-[180px] bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 animate-fade-in">
                {[
                  { value: "all" as TimeframeOption, label: "Todas" },
                  { value: "today" as TimeframeOption, label: "Hoy" },
                  {
                    value: "last7days" as TimeframeOption,
                    label: "Últimos 7 días",
                  },
                  {
                    value: "last30days" as TimeframeOption,
                    label: "Últimos 30 días",
                  },
                  {
                    value: "last90days" as TimeframeOption,
                    label: "Últimos 90 días",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setSelectedTimeframe(opt.value);
                      setTimeframeDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-[length:var(--text-body)] transition-colors ${
                      selectedTimeframe === opt.value
                        ? "bg-neutral-100 text-neutral-900 font-medium"
                        : "text-neutral-600 hover:bg-neutral-100"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Brand — custom dropdown (solo si hay >1 marca) */}
          {brands.length > 1 && (
            <div ref={brandDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => {
                  setBrandDropdownOpen(!brandDropdownOpen);
                  setTimeframeDropdownOpen(false);
                }}
                className="h-[var(--btn-h)] px-3 inline-flex items-center justify-between gap-2 border border-neutral-200 rounded-md bg-surface-card hover:bg-surface-elevated text-[length:var(--text-body)] font-medium text-neutral-900 shadow-sm outline-none transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  {selectedBrandId && (() => {
                    const b = brands.find((bb) => bb.id === selectedBrandId);
                    if (!b?.logo_url) return null;
                    const src = b.logo_url.includes("supabase.co")
                      ? `${b.logo_url}?width=32&height=32`
                      : b.logo_url;
                    return (
                      <img
                        src={src}
                        alt=""
                        className="w-5 h-5 rounded-[4px] object-contain bg-white border border-neutral-200 shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "/images/estudio-atlas-logo-solo.svg";
                        }}
                      />
                    );
                  })()}
                  <span className="truncate">
                    {selectedBrandId
                      ? brands.find((b) => b.id === selectedBrandId)?.name ||
                        "Marca"
                      : "Todas las marcas"}
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-neutral-400 transition-transform ${brandDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              {brandDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full min-w-[220px] bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 animate-fade-in overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBrandId(null);
                      if (
                        allImages.length > 0 &&
                        allImages.length % PAGE_SIZE === 0
                      )
                        setHasMoreImages(true);
                      setBrandDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-[length:var(--text-body)] transition-colors ${
                      selectedBrandId === null
                        ? "bg-neutral-100 text-neutral-900 font-medium"
                        : "text-neutral-600 hover:bg-neutral-100"
                    }`}
                  >
                    Todas las marcas
                  </button>
                  <div className="max-h-60 overflow-y-auto scrollbar-thin">
                    {brands.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => {
                          setSelectedBrandId(b.id);
                          if (
                            allImages.length > 0 &&
                            allImages.length % PAGE_SIZE === 0
                          )
                            setHasMoreImages(true);
                          setBrandDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[length:var(--text-body)] transition-colors ${
                          selectedBrandId === b.id
                            ? "bg-neutral-100 text-neutral-900 font-medium"
                            : "text-neutral-600 hover:bg-neutral-100"
                        }`}
                      >
                        <img
                          src={
                            b.logo_url
                              ? (b.logo_url.includes("supabase.co")
                                  ? `${b.logo_url}?width=32&height=32`
                                  : b.logo_url)
                              : "/images/estudio-atlas-logo-solo.svg"
                          }
                          alt=""
                          className="w-5 h-5 rounded-[4px] object-contain bg-white border border-neutral-200 shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/images/estudio-atlas-logo-solo.svg";
                          }}
                        />
                        <span className="truncate">{b.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Content type — custom dropdown */}
          <div ref={contentTypeDropdownRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setContentTypeDropdownOpen(!contentTypeDropdownOpen);
                setTimeframeDropdownOpen(false);
                setVariantDropdownOpen(false);
              }}
              className={`h-[var(--btn-h)] px-4 inline-flex items-center justify-between gap-2 border rounded-md text-[length:var(--text-body)] font-medium shadow-sm outline-none transition-colors cursor-pointer whitespace-nowrap ${
                contentTypeFilter !== "all"
                  ? "bg-brand-primary hover:bg-brand-primary-hover text-white border-brand-primary"
                  : "bg-surface-card hover:bg-surface-elevated text-neutral-900 border-neutral-200"
              }`}
            >
              <span>
                {
                  { all: "Todo", images: "Imágenes", videos: "Videos" }[
                    contentTypeFilter
                  ]
                }
              </span>
              <ChevronDown
                className={`w-4 h-4 ${contentTypeFilter !== "all" ? "text-white/70" : "text-neutral-400"} transition-transform ${contentTypeDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>
            {contentTypeDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full min-w-[140px] bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 animate-fade-in">
                {[
                  { value: "all" as ContentTypeOption, label: "Todo" },
                  { value: "images" as ContentTypeOption, label: "Imágenes" },
                  { value: "videos" as ContentTypeOption, label: "Videos" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setContentTypeFilter(opt.value);
                      setContentTypeDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-[length:var(--text-body)] transition-colors ${
                      contentTypeFilter === opt.value
                        ? "bg-neutral-100 text-neutral-900 font-medium"
                        : "text-neutral-600 hover:bg-neutral-100"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Favorites toggle */}
          <button
            type="button"
            onClick={() => {
              setOnlyFavorites((prev) => !prev);
              if (allImages.length > 0 && allImages.length % PAGE_SIZE === 0) {
                setHasMoreImages(true);
              }
            }}
            className={`h-[var(--btn-h)] px-5 inline-flex items-center gap-2 text-[length:var(--text-body)] font-medium border rounded-md shadow-sm transition-colors cursor-pointer ${
              onlyFavorites
                ? "bg-brand-primary hover:bg-brand-primary-hover text-white border-brand-primary"
                : "bg-surface-card text-neutral-900 border-neutral-200 hover:bg-neutral-100"
            }`}
          >
            <Heart
              className={`w-3.5 h-3.5 ${onlyFavorites ? "fill-current" : ""}`}
            />
            Favoritos
          </button>

          {/* Seleccionar (descarga masiva) */}
          <button
            type="button"
            onClick={() => setSelectionMode((prev) => !prev)}
            className={`h-[var(--btn-h)] px-5 inline-flex items-center gap-2 text-[length:var(--text-body)] font-medium border rounded-md shadow-sm transition-colors cursor-pointer ${
              selectionMode
                ? "bg-brand-primary hover:bg-brand-primary-hover text-white border-brand-primary"
                : "bg-surface-card text-neutral-900 border-neutral-200 hover:bg-neutral-100"
            }`}
          >
            <GalleryVerticalEnd className="w-3.5 h-3.5" />
            Seleccionar
          </button>
        </div>

        {/* Mobile Filter Panel — Apple HIG sheet */}
        {showMobileFilters &&
          createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${filterMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={() => {
                  setFilterMenuAnimated(false);
                  setTimeout(() => setShowMobileFilters(false), 300);
                }}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    filterMenuDragY > 0
                      ? `translateY(${filterMenuDragY}px)`
                      : filterMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    filterMenuDragY === 0 ? "transform 0.3s ease-out" : "none",
                }}
              >
                {/* Drag handle */}
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={(e) => {
                    filterMenuDragStartRef.current = e.touches[0].clientY;
                  }}
                  onTouchMove={(e) => {
                    const diff =
                      e.touches[0].clientY - filterMenuDragStartRef.current;
                    if (diff > 0) setFilterMenuDragY(diff);
                  }}
                  onTouchEnd={() => {
                    if (filterMenuDragY > 120) {
                      setFilterMenuAnimated(false);
                      setTimeout(() => setShowMobileFilters(false), 300);
                    }
                    setFilterMenuDragY(0);
                  }}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                {/* Header: Cancelar / Título / Aceptar */}
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFilterMenuAnimated(false);
                      setTimeout(() => setShowMobileFilters(false), 300);
                    }}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Filtros
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTimeframe(mobileSelectedTimeframe);
                      setSelectedVariant(mobileSelectedVariant);
                      setOnlyFavorites(mobileOnlyFavorites);
                      setContentTypeFilter(mobileContentType);
                      setSelectedBrandId(mobileSelectedBrandId);
                      if (
                        allImages.length > 0 &&
                        allImages.length % PAGE_SIZE === 0
                      ) {
                        setHasMoreImages(true);
                      }
                      setFilterMenuAnimated(false);
                      setTimeout(() => setShowMobileFilters(false), 300);
                    }}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Aceptar
                  </button>
                </div>
                {/* Filter content */}
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {/* Favoritos toggle */}
                  <p className="text-[13px] font-medium text-brand-black/50 uppercase tracking-wider mb-2 px-1">
                    Favoritos
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden mb-6">
                    <button
                      type="button"
                      onClick={() => setMobileOnlyFavorites((prev) => !prev)}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                          favorite
                        </span>
                        Solo favoritos
                      </span>
                      <div
                        className={`relative inline-flex h-[31px] w-[51px] flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${mobileOnlyFavorites ? "bg-[#34C759]" : "bg-neutral-200"}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-[27px] w-[27px] mt-[2px] transform rounded-full bg-white shadow ring-0 transition duration-200 ${mobileOnlyFavorites ? "translate-x-[22px]" : "translate-x-[2px]"}`}
                        />
                      </div>
                    </button>
                  </div>

                  {/* Tipo de contenido */}
                  <p className="text-[13px] font-medium text-brand-black/50 uppercase tracking-wider mb-2 px-1">
                    Tipo de contenido
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden mb-6">
                    {[
                      {
                        value: "all" as ContentTypeOption,
                        label: "Todo",
                        icon: "apps",
                      },
                      {
                        value: "images" as ContentTypeOption,
                        label: "Imágenes",
                        icon: "image",
                      },
                      {
                        value: "videos" as ContentTypeOption,
                        label: "Videos",
                        icon: "videocam",
                      },
                    ].map((opt, i, arr) => (
                      <React.Fragment key={opt.value}>
                        <button
                          type="button"
                          onClick={() => setMobileContentType(opt.value)}
                          className={`w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between ${
                            mobileContentType === opt.value
                              ? "bg-neutral-100"
                              : ""
                          }`}
                        >
                          <span className="flex items-center gap-3">
                            <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                              {opt.icon}
                            </span>
                            {opt.label}
                          </span>
                          {mobileContentType === opt.value && (
                            <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                          )}
                        </button>
                        {i < arr.length - 1 && (
                          <div className="ml-[46px] h-px bg-black/[0.08]" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Período de tiempo */}
                  <p className="text-[13px] font-medium text-brand-black/50 uppercase tracking-wider mb-2 px-1">
                    Período de tiempo
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden mb-6">
                    {[
                      {
                        value: "all" as TimeframeOption,
                        label: "Todas",
                        icon: "apps",
                      },
                      {
                        value: "today" as TimeframeOption,
                        label: "Hoy",
                        icon: "today",
                      },
                      {
                        value: "last7days" as TimeframeOption,
                        label: "Últimos 7 días",
                        icon: "date_range",
                      },
                      {
                        value: "last30days" as TimeframeOption,
                        label: "Últimos 30 días",
                        icon: "calendar_month",
                      },
                      {
                        value: "last90days" as TimeframeOption,
                        label: "Últimos 90 días",
                        icon: "event_note",
                      },
                    ].map((opt, i, arr) => (
                      <React.Fragment key={opt.value}>
                        <button
                          type="button"
                          onClick={() => setMobileSelectedTimeframe(opt.value)}
                          className={`w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between ${
                            mobileSelectedTimeframe === opt.value
                              ? "bg-neutral-100"
                              : ""
                          }`}
                        >
                          <span className="flex items-center gap-3">
                            <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                              {opt.icon}
                            </span>
                            {opt.label}
                          </span>
                          {mobileSelectedTimeframe === opt.value && (
                            <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                          )}
                        </button>
                        {i < arr.length - 1 && (
                          <div className="ml-[46px] h-px bg-black/[0.08]" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Marca (solo si hay >1 marca) */}
                  {brands.length > 1 && (
                    <>
                      <p className="text-[13px] font-medium text-brand-black/50 uppercase tracking-wider mb-2 px-1">
                        Marca
                      </p>
                      <div className="rounded-[13px] bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setMobileSelectedBrandId(null)}
                          className={`w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between ${
                            mobileSelectedBrandId === null
                              ? "bg-neutral-100"
                              : ""
                          }`}
                        >
                          <span className="flex items-center gap-3">
                            <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                              apps
                            </span>
                            Todas las marcas
                          </span>
                          {mobileSelectedBrandId === null && (
                            <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                          )}
                        </button>
                        {brands.map((b) => (
                          <React.Fragment key={b.id}>
                            <div className="ml-[46px] h-px bg-black/[0.08]" />
                            <button
                              type="button"
                              onClick={() => setMobileSelectedBrandId(b.id)}
                              className={`w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between ${
                                mobileSelectedBrandId === b.id
                                  ? "bg-neutral-100"
                                  : ""
                              }`}
                            >
                              <span className="flex items-center gap-3">
                                <img
                                  src={
                                    b.logo_url
                                      ? (b.logo_url.includes("supabase.co")
                                          ? `${b.logo_url}?width=32&height=32`
                                          : b.logo_url)
                                      : "/images/estudio-atlas-logo-solo.svg"
                                  }
                                  alt=""
                                  className="w-5 h-5 rounded-[4px] object-contain bg-white border border-neutral-200 shrink-0"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src =
                                      "/images/estudio-atlas-logo-solo.svg";
                                  }}
                                />
                                {b.name}
                              </span>
                              {mobileSelectedBrandId === b.id && (
                                <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                              )}
                            </button>
                          </React.Fragment>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>,
            document.body,
          )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-error-soft border border-error/20 rounded-md flex items-center gap-2 text-error-text text-[14px] font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-lg overflow-hidden">
              <div className="w-full aspect-[4/5] bg-neutral-200 animate-pulse" />
            </div>
          ))}
        </div>
      ) : displayImages.length === 0 ? (
        <div className="text-center py-16">
          <ImageIcon className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-[15px] text-neutral-400 font-medium">
            {searchQuery.trim()
              ? "No se encontraron resultados"
              : "No se encontraron imágenes generadas"}
          </p>
          {searchQuery.trim() && (
            <button
              onClick={() => setSearchQuery("")}
              className="mt-2 text-[14px] text-[#1644EB] active:opacity-50"
            >
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {displayImages.map((image) => {
              const isSelected = selectedIds.has(image.id);
              return (
              <div
                key={image.id}
                data-image-id={image.id}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={
                  selectionMode
                    ? {
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        WebkitUserDrag: "none",
                      } as React.CSSProperties
                    : undefined
                }
                className={`group relative rounded-lg overflow-hidden cursor-pointer ${
                  selectionMode && isSelected
                    ? "ring-2 ring-brand-primary"
                    : ""
                }`}
                onClick={(e) => {
                  // Si vino de un long-press, cancelamos el click
                  if (longPressTriggeredRef.current) {
                    longPressTriggeredRef.current = false;
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  if (selectionMode) {
                    toggleSelected(image.id);
                    return;
                  }
                  // Mobile: open detail view with hero animation; Desktop: open lightbox
                  if (window.innerWidth < 640) {
                    handleGridImageTap(image, e);
                  } else {
                    handleImageClick(image);
                  }
                }}
                onMouseDown={(e) => {
                  if (!selectionMode) return;
                  // Solo botón izquierdo
                  if (e.button !== 0) return;
                  dragSelectRef.current = {
                    active: false,
                    toggleTo: selectedIds.has(image.id) ? "deselect" : "select",
                    visited: new Set([image.id]),
                    startX: e.clientX,
                    startY: e.clientY,
                    hasMoved: false,
                  };
                }}
                onTouchStart={(e) => {
                  if (selectionMode) {
                    // Iniciar tracking de drag-select
                    const touch = e.touches[0];
                    dragSelectRef.current = {
                      active: false,
                      toggleTo: selectedIds.has(image.id)
                        ? "deselect"
                        : "select",
                      visited: new Set([image.id]),
                      startX: touch.clientX,
                      startY: touch.clientY,
                      hasMoved: false,
                    };
                    return;
                  }
                  longPressTriggeredRef.current = false;
                  longPressTimerRef.current = setTimeout(() => {
                    longPressTriggeredRef.current = true;
                    setSelectionMode(true);
                    setSelectedIds(new Set([image.id]));
                    // Feedback háptico si está disponible
                    if (navigator.vibrate) navigator.vibrate(40);
                  }, 500);
                }}
                onTouchEnd={() => {
                  if (longPressTimerRef.current) {
                    clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = null;
                  }
                  // dragSelectRef.current se limpia en el handler global onTouchEnd
                }}
                onTouchMove={() => {
                  if (longPressTimerRef.current) {
                    clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = null;
                  }
                }}
              >
                <div className="aspect-[4/5] relative">
                  {/* Selection checkbox (sólo visible en modo selección) */}
                  {selectionMode && (
                    <div className="absolute top-2 left-2 z-30 pointer-events-none">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${
                          isSelected
                            ? "bg-brand-primary border-brand-primary"
                            : "bg-white/80 backdrop-blur-sm border-white/90"
                        }`}
                      >
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                        )}
                      </div>
                    </div>
                  )}
                  {/* Favorito (solo lectura) — indicador en las ya marcadas.
                      Ya no se pueden marcar nuevas como favoritas. */}
                  {!selectionMode && image.favorito && (
                    <div
                      className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center"
                      aria-label="Favorita"
                    >
                      <Heart className="w-3.5 h-3.5 text-red-500 fill-current" />
                    </div>
                  )}
                  {isVideoItem(image) &&
                  !(
                    image.thumbnailUrl &&
                    !image.thumbnailUrl.toLowerCase().includes(".mp4")
                  ) ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                      <div className="w-12 h-12 bg-neutral-400 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-white fill-white" />
                      </div>
                    </div>
                  ) : (
                    (image.thumbnailUrl || image.signedUrl) && (
                      <>
                        {loadingThumbnails[image.id] && (
                          <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 z-10">
                            <div className="w-6 h-6 border-2 border-neutral-200 border-t-brand-primary rounded-full animate-spin"></div>
                          </div>
                        )}
                        <img
                          src={image.thumbnailUrl || image.signedUrl}
                          alt={`Generated image ${image.id}`}
                          className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                            loadingThumbnails[image.id]
                              ? "opacity-40"
                              : "opacity-100"
                          }`}
                          loading="lazy"
                          width="300"
                          height="300"
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                          onLoad={() => {
                            setLoadingThumbnails((prev) => ({
                              ...prev,
                              [image.id]: false,
                            }));
                          }}
                          onError={() => {
                            console.error(
                              `Failed to load image ${image.id}, attempting to refresh URL`,
                            );
                            setLoadingThumbnails((prev) => ({
                              ...prev,
                              [image.id]: true,
                            }));
                            refreshImageUrl(image);
                          }}
                        />
                        {isVideoItem(image) && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                              <Play className="w-6 h-6 text-white fill-white" />
                            </div>
                          </div>
                        )}
                      </>
                    )
                  )}
                  {/* Gradient overlay with text integrated — UICatalog style */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 pointer-events-none">
                    <p className="text-[14px] font-medium text-white truncate">
                      {image.variante_nombre}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3 text-white/60" />
                      <p className="text-[12px] text-white/60">
                        {formatSmartDate(image.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Infinite scroll loading indicator */}
          {hasMoreImages && displayImages.length > 0 && (
            <div ref={loadMoreRef} className="flex justify-center mt-8 py-4">
              {loadingMore && (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-neutral-200 border-t-brand-primary rounded-full animate-spin" />
                  <span className="text-[14px] text-neutral-400">
                    Cargando más imágenes...
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ==================== MOBILE DETAIL VIEW (Apple Photos style) ==================== */}
      {mobileView === "detail" && (
        <div
          className="sm:hidden fixed inset-0 z-40 flex flex-col bg-[#F2F2F7]"
          style={{ top: "3.5rem" }}
        >
          {/* Image viewer or crop canvas — fills all available space above buttons */}
          {showCropModal &&
          currentViewImage &&
          !isVideoItem(currentViewImage) &&
          !isDesktopCrop ? (
            <div className="flex-1 min-h-0 relative">
              <div className="absolute inset-0">
                <ImageCropCanvas cropState={cropState} />
              </div>
            </div>
          ) : (
            <div
              ref={heroImageRef}
              className="flex-1 min-h-0 relative bg-[#F2F2F7]"
            >
              {displayImages.length > 0 ? (
                <div
                  ref={scrollContainerRef}
                  onScroll={handleDetailScroll}
                  className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    WebkitOverflowScrolling: "touch",
                    opacity:
                      heroAnimating && heroDirection === "forward" ? 0 : 1,
                  }}
                >
                  {displayImages.map((image) => {
                    const isVideo = isVideoItem(image);
                    return (
                      <div
                        key={`detail-snap-${image.id}`}
                        className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center p-3"
                      >
                        {isVideo ? (
                          <video
                            src={image.signedUrl}
                            controls
                            playsInline
                            className="max-w-full max-h-full object-contain rounded-2xl"
                          />
                        ) : (
                          <img
                            src={
                              image.upscaled_path &&
                              image.upscaledSignedUrl &&
                              !showingOriginalAfterUpscale
                                ? image.upscaledSignedUrl
                                : image.signedUrl || image.thumbnailUrl
                            }
                            alt={`Generated image ${image.id}`}
                            className="max-w-full max-h-full object-contain rounded-2xl"
                            onError={() => refreshImageUrl(image)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-500">
                  <p className="text-[15px]">No hay imágenes para mostrar</p>
                </div>
              )}
              {showUpscaleToggle && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
                  <UpscaleComparisonToggle variant="mobile" />
                </div>
              )}
            </div>
          )}

          {/* Action bar — static below image */}
          <div
            className="flex-shrink-0 px-4 pt-2 bg-[#F2F2F7]"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            }}
          >
            {showCropModal &&
            currentViewImage &&
            !isVideoItem(currentViewImage) &&
            !isDesktopCrop ? (
              <ImageCropControls
                cropState={cropState}
                onCancel={() => setShowCropModal(false)}
                variant="mobile"
              />
            ) : (
              <div className="relative">
                {/* Action buttons grid: 2 rows × 3 columns */}
                <div className="grid grid-cols-3 gap-2">
                  {/* Descargar — primary blue */}
                  <button
                    onClick={(e) => {
                      if (currentViewImage) handleDownload(currentViewImage, e);
                    }}
                    disabled={!currentViewImage}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${currentViewImage ? "bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
                  >
                    <Download className="w-5 h-5" />
                    <span>Descargar</span>
                  </button>
                  {/* Editar */}
                  {currentViewImage && !isVideoItem(currentViewImage) && (
                    <button
                      onClick={(e) => handleEditImage(currentViewImage, e)}
                      className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated"
                    >
                      <Pencil className="w-5 h-5" />
                      <span>Editar</span>
                    </button>
                  )}
                  {/* Carrusel */}
                  {currentViewImage && !isVideoItem(currentViewImage) && (
                    <button
                      onClick={() => handleCarruselClick()}
                      className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated"
                    >
                      <Copy className="w-5 h-5" />
                      <span>Carrusel</span>
                    </button>
                  )}
                  {/* Animar */}
                  {currentViewImage && !isVideoItem(currentViewImage) && (
                    <button
                      onClick={() => openVideoMenu(currentViewImage)}
                      disabled={!currentViewImage}
                      className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated"
                    >
                      <Play className="w-5 h-5" />
                      <span>Animar</span>
                    </button>
                  )}
                  {/* Más */}
                  <div ref={mobileDetailMoreRef} className="relative">
                    <button
                      onClick={() =>
                        setShowMobileDetailMore(!showMobileDetailMore)
                      }
                      disabled={!currentViewImage}
                      className={`w-full flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${!currentViewImage ? "bg-neutral-200 text-neutral-400 cursor-not-allowed" : "bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated"}`}
                    >
                      <MoreHorizontal className="w-5 h-5" />
                      <span>Más</span>
                    </button>
                    {showMobileDetailMore && (
                      <div
                        className="absolute bottom-full mb-2 right-0 min-w-[200px] bg-white rounded-[14px] py-1.5 px-1.5 z-30 animate-fade-in"
                        style={{
                          boxShadow:
                            "0 8px 40px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.06)",
                        }}
                      >
                        {currentViewImage && !isVideoItem(currentViewImage) && (
                          <button
                            onClick={() => {
                              if (currentViewImage) {
                                handleShowDescription(
                                  undefined,
                                  currentViewImage,
                                );
                                setShowMobileDetailMore(false);
                              }
                            }}
                            disabled={isGeneratingDescription}
                            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-black/85 hover:bg-[#F2F2F7] transition-colors"
                          >
                            <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-[#F2F2F7] text-[#3C3C43]/70">
                              <Instagram className="w-3.5 h-3.5" />
                            </span>
                            Descripción
                          </button>
                        )}
                        {currentViewImage && !isVideoItem(currentViewImage) && (
                          <button
                            onClick={() => {
                              setShowMobileDetailMore(false);
                              setShowCropModal(true);
                            }}
                            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-black/85 hover:bg-[#F2F2F7] transition-colors"
                          >
                            <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-[#F2F2F7] text-[#3C3C43]/70">
                              <Crop className="w-3.5 h-3.5" />
                            </span>
                            Recortar
                          </button>
                        )}
                        {currentViewImage && !isVideoItem(currentViewImage) && (
                          <button
                            onClick={() => {
                              setShowMobileDetailMore(false);
                              openUpscaleMenu(currentViewImage);
                            }}
                            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-black/85 hover:bg-[#F2F2F7] transition-colors"
                          >
                            <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-[#F2F2F7] text-[#3C3C43]/70">
                              <Maximize className="w-3.5 h-3.5" />
                            </span>
                            Mejorar resolución
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (currentViewImage) {
                              showDeleteConfirmation(currentViewImage);
                              setShowMobileDetailMore(false);
                            }
                          }}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-red-500 hover:bg-[#F2F2F7] transition-colors"
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-red-50 text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </span>
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Description panel */}
            <div className="relative">
              {showDescriptionPanel && currentViewImage && (
                <div className="absolute bottom-full left-0 right-0 mb-2 z-20">
                  <div className="relative bg-surface-card rounded-lg shadow-lg border border-neutral-200 p-4">
                    <button
                      onClick={() => setShowDescriptionPanel(false)}
                      className="absolute top-3 right-3 w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center"
                      aria-label="Cerrar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {isGeneratingDescription ? (
                      <div className="flex items-center gap-2 text-neutral-600">
                        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                        <span className="text-[15px]">
                          Generando descripción...
                        </span>
                        <div className="absolute left-3 right-3 bottom-0 h-1.5 rounded-full overflow-hidden bg-neutral-200">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: progressActive ? "100%" : "0%",
                              transition:
                                "width 20s cubic-bezier(0, 0, 0.2, 1)",
                              background:
                                "linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)",
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-[17px] font-semibold text-brand-black mb-2">
                          Descripción para post
                        </h3>
                        <div className="text-[15px] text-neutral-600 whitespace-pre-wrap pr-6">
                          {postDescription}
                        </div>
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => copyDescription("panel")}
                            className={`${copiedPanel ? "bg-green-600 hover:bg-green-700" : "bg-brand-primary hover:bg-brand-primary-hover"} h-9 px-4 text-white rounded-md shadow-sm text-[15px] font-medium flex items-center gap-2`}
                          >
                            {copiedPanel ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                            {copiedPanel ? "Copiado" : "Copiar"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero animation overlay — Apple Photos style position transition (forward + reverse) */}
      {heroAnimating &&
        heroRect &&
        currentViewImage &&
        (() => {
          const isEnd = heroPhase === "end";
          const spring = "cubic-bezier(0.2, 0.9, 0.3, 1)";
          const dur = "400ms";
          const transition = `top ${dur} ${spring}, left ${dur} ${spring}, width ${dur} ${spring}, height ${dur} ${spring}, border-radius ${dur} ${spring}`;

          // Both directions: heroRect = start position, heroTargetRect = end position
          const startR = heroRect;
          const endR = heroTargetRect || heroRect;

          return (
            <div
              className="pointer-events-none"
              style={{
                position: "fixed",
                zIndex: 100,
                overflow: "hidden",
                borderRadius: 16,
                top: isEnd ? endR.y : startR.y,
                left: isEnd ? endR.x : startR.x,
                width: isEnd ? endR.width : startR.width,
                height: isEnd ? endR.height : startR.height,
                transition: isEnd ? transition : "none",
                willChange: "top, left, width, height",
              }}
            >
              <img
                src={
                  currentViewImage.signedUrl ||
                  currentViewImage.thumbnailUrl ||
                  ""
                }
                alt="Hero transition"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>
          );
        })()}

      {/* Instagram Post Preview Modal */}
      {showInstagramPreview && selectedImage && (
        <div className="fixed inset-0 bg-surface-bg z-50 flex flex-col">
          {/* Header section */}
          <div className="px-4 py-3 bg-surface-card shadow-sm z-20 sticky top-0 flex justify-between items-center">
            <h2 className="text-[17px] font-semibold text-neutral-900">
              Vista previa de Instagram
            </h2>
            <button
              onClick={() => setShowInstagramPreview(false)}
              className="w-10 h-10 inline-flex items-center justify-center text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-md transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Main content area with scrolling */}
          <div className="flex-1 overflow-y-auto py-4 px-4 mt-2 relative">
            <div className="flex flex-col md:flex-row md:items-start md:justify-center md:space-x-6 max-w-5xl mx-auto pb-6">
              {/* Instagram post container */}
              <div className="bg-surface-card rounded-lg border border-neutral-200 w-full max-w-sm mx-auto md:mx-0 overflow-hidden mb-6 md:mb-0 flex flex-col">
                {/* Post header with username and profile pic */}
                <div className="flex items-center p-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-neutral-200 flex-shrink-0">
                    {companyLogo ? (
                      <img
                        src={companyLogo}
                        alt="Company logo"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-brand-primary flex items-center justify-center text-white font-semibold">
                        {instagramUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-[length:var(--text-body)] text-neutral-900">
                      {instagramUsername}
                    </p>
                  </div>
                </div>
                {/* Post image with edge-to-edge display */}
                <div
                  ref={imageContainerRef as any}
                  className="w-full overflow-hidden"
                  style={{ margin: 0, padding: 0, borderRadius: 0 }}
                >
                  <img
                    src={selectedImage.signedUrl || selectedImage.thumbnailUrl}
                    alt="Instagram post"
                    className="w-full"
                    style={{
                      width: "100%",
                      maxHeight: "min(700px, calc(100vh - 200px))",
                      display: "block",
                      margin: 0,
                      padding: 0,
                      borderRadius: 0,
                    }}
                    onLoad={(e) => {
                      const img = e.target as HTMLImageElement;
                      const aspectRatio = img.naturalWidth / img.naturalHeight;
                      const container = imageContainerRef.current;
                      if (!container) return;
                      if (aspectRatio === 1) {
                        container.style.aspectRatio = "1/1";
                        img.style.objectFit = "contain";
                      } else if (aspectRatio > 1 && aspectRatio <= 1.91) {
                        container.style.aspectRatio = aspectRatio.toString();
                        img.style.objectFit = "contain";
                      } else if (aspectRatio < 1 && aspectRatio >= 0.8) {
                        container.style.aspectRatio = aspectRatio.toString();
                        img.style.objectFit = "contain";
                      } else if (aspectRatio < 0.8) {
                        container.style.aspectRatio = "0.8";
                        img.style.height = "100%";
                        img.style.width = "auto";
                        img.style.maxWidth = "none";
                        img.style.minWidth = "100%";
                        img.style.objectFit = "cover";
                        img.style.objectPosition = "center";
                        const scaledWidth = img.naturalHeight * 0.8;
                        if (scaledWidth < img.naturalWidth) {
                          img.style.objectPosition = "50% 50%";
                        }
                      } else {
                        container.style.aspectRatio = "1.91";
                        img.style.objectFit = "contain";
                      }
                    }}
                  />
                </div>
                {/* Post interaction area */}
                <div className="p-3 border-t border-neutral-100 mt-auto">
                  <div className="flex space-x-4">
                    <button className="text-neutral-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
                        />
                      </svg>
                    </button>
                    <button className="text-neutral-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z"
                        />
                      </svg>
                    </button>
                    <button className="text-neutral-900">
                      <Share2 className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="mt-2">
                    <p className="font-semibold text-[length:var(--text-body)] text-neutral-900">
                      127 Me gusta
                    </p>
                  </div>
                  <div className="mt-1">
                    {isEditingDescription ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full p-2.5 text-[length:var(--text-body)] border border-neutral-200 rounded-md focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none text-neutral-900 placeholder:text-neutral-400 bg-surface-card min-h-[100px]"
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          placeholder="Escribe la descripción aquí..."
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setPostDescription(editedDescription);
                              setIsEditingDescription(false);
                            }}
                            className="h-[var(--btn-h-sm)] px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[length:var(--text-body)] font-medium rounded-md shadow-sm transition-colors"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setEditedDescription(postDescription);
                              setIsEditingDescription(false);
                            }}
                            className="h-[var(--btn-h-sm)] px-4 bg-surface-card hover:bg-neutral-100 text-neutral-900 text-[length:var(--text-body)] font-medium border border-neutral-200 rounded-md shadow-sm transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[length:var(--text-body)] text-neutral-900">
                        <span className="font-semibold mr-1">
                          {instagramUsername}
                        </span>
                        {postDescription}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Action buttons - to the right on desktop, below on mobile */}
              <div className="w-full max-w-sm mx-auto md:mx-0 md:w-60 md:flex-shrink-0 md:flex md:flex-col md:sticky md:top-1/2 md:-translate-y-1/2 space-y-2">
                <button
                  onClick={() => {
                    if (!isInstagramConnected) {
                      setShowConnectPrompt(true);
                      return;
                    }
                    handlePostToInstagram();
                  }}
                  className="w-full h-[var(--btn-h)] rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white font-medium text-[length:var(--text-body)] shadow-sm transition-colors flex items-center justify-center gap-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isGeneratingDescription || isPostingToInstagram}
                >
                  {isPostingToInstagram ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Publicando...</span>
                    </>
                  ) : instagramPostSuccess ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Publicado con éxito</span>
                    </>
                  ) : (
                    <>
                      {isInstagramConnected ? (
                        <Instagram className="w-4 h-4" />
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                      <span>Publicar en Instagram</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditedDescription(postDescription);
                    setIsEditingDescription(true);
                  }}
                  className="w-full h-[var(--btn-h)] rounded-md bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 font-medium text-[length:var(--text-body)] shadow-sm transition-colors flex items-center justify-center gap-2 px-5"
                >
                  <Pencil className="w-4 h-4" />
                  <span>Editar Descripción</span>
                </button>
                <button
                  onClick={() => handleDownload(selectedImage)}
                  className="w-full h-[var(--btn-h)] rounded-md bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 font-medium text-[length:var(--text-body)] shadow-sm transition-colors flex items-center justify-center gap-2 px-5"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar</span>
                </button>
                <button
                  onClick={() => copyDescription("preview")}
                  className={`w-full h-[var(--btn-h)] rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors flex items-center justify-center gap-2 px-5 ${copiedPreview ? "bg-success text-white" : "bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200"}`}
                >
                  {copiedPreview ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  <span>
                    {copiedPreview ? "Copiado" : "Copiar Descripción"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Image View */}
      {selectedImage &&
      (selectedImage.signedUrl || selectedImage.thumbnailUrl) &&
      !showInstagramPreview ? (
        <div
          className={`fixed inset-0 z-50 transition-opacity duration-200 ease-out ${lightboxOpen ? "opacity-100" : "opacity-0"}`}
          onClick={closeLightbox}
        >
          {/* Background: blurred image on desktop, dark overlay on mobile */}
          <div
            className={`absolute inset-0 bg-black/90 ${isVideoItem(selectedImage) ? "md:bg-black/60 md:backdrop-blur-xl" : "md:bg-black/40"}`}
          />
          {!isVideoItem(selectedImage) && (
            <img
              src={selectedImage.thumbnailUrl || selectedImage.signedUrl}
              alt=""
              aria-hidden="true"
              className="hidden md:block absolute inset-0 w-full h-full object-cover scale-125"
              style={{ filter: "blur(30px) saturate(1.2) brightness(0.4)" }}
            />
          )}

          {/* Mobile layout: same as before — absolute positioned everything */}
          <div
            className="md:hidden relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Navigation Arrows */}
            <div className="absolute inset-y-0 left-4 flex items-center z-20">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (filteredImages.length > 1 && selectedImage) {
                    const currentIndex = filteredImages.findIndex(
                      (img) => img.id === selectedImage.id,
                    );
                    const prevIndex =
                      currentIndex <= 0
                        ? filteredImages.length - 1
                        : currentIndex - 1;
                    const prevImage = filteredImages[prevIndex];
                    if (prevImage) {
                      setSelectedImage(prevImage);
                      setZoomLevel(1);
                      setDragOffset({ x: 0, y: 0 });
                      setShowMoreMenu(false);
                    }
                  }
                }}
                className="w-10 h-10 inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-md backdrop-blur-sm transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            </div>
            <div className="absolute inset-y-0 right-4 flex items-center z-20">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (filteredImages.length > 1 && selectedImage) {
                    const currentIndex = filteredImages.findIndex(
                      (img) => img.id === selectedImage.id,
                    );
                    const nextIndex =
                      currentIndex === -1 ||
                      currentIndex + 1 >= filteredImages.length
                        ? 0
                        : currentIndex + 1;
                    const nextImage = filteredImages[nextIndex];
                    if (nextImage) {
                      setSelectedImage(nextImage);
                      setZoomLevel(1);
                      setDragOffset({ x: 0, y: 0 });
                      setShowMoreMenu(false);
                    }
                  }
                }}
                className="w-10 h-10 inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-md backdrop-blur-sm transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
            {/* Image area */}
            <div className="absolute inset-0 overflow-hidden flex items-center justify-center z-10">
              {expandedImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              {showUpscaleToggle && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
                  <UpscaleComparisonToggle variant="mobile" />
                </div>
              )}
              {isVideoItem(selectedImage) ? (
                !isDesktopCrop ? (
                  <video
                    key={`mobile-video-${selectedImage.id}`}
                    src={selectedImage.signedUrl}
                    controls
                    playsInline
                    autoPlay
                    className="pointer-events-auto rounded-lg"
                    style={{ maxWidth: "90%", maxHeight: "80%" }}
                    onLoadStart={() => setExpandedImageLoading(true)}
                    onLoadedData={() => setExpandedImageLoading(false)}
                  >
                    Tu navegador no soporta la reproducción de video.
                  </video>
                ) : null
              ) : (
                <img
                  src={
                    showUpscaleToggle && !showingOriginalAfterUpscale
                      ? selectedImage.upscaledSignedUrl!
                      : selectedImage.signedUrl || selectedImage.thumbnailUrl
                  }
                  alt={`Full screen view of ${selectedImage.file_name}`}
                  className="pointer-events-none rounded-lg"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${dragOffset.x}px, ${dragOffset.y}px)`,
                    // Alto fijo (no `auto`): así el tamaño mostrado lo define el
                    // aspect-ratio, idéntico en la versión `opt` y la `full`, y
                    // la `opt` ya no se renderiza chica antes de cargar la HD.
                    maxWidth: "90%",
                    height: "90%",
                    width: "auto",
                    objectFit: "contain",
                    transition: isDragging ? "none" : "transform 0.2s ease-out",
                    opacity: expandedImageLoading ? 0 : 1,
                  }}
                  onLoadStart={() => setExpandedImageLoading(true)}
                  onLoad={() => setExpandedImageLoading(false)}
                  onError={() => {
                    console.error(
                      `Failed to load expanded image ${selectedImage.id}, attempting to refresh URL`,
                    );
                    setExpandedImageLoading(true);
                    refreshImageUrl(selectedImage);
                  }}
                />
              )}
            </div>
            {/* Mobile close button */}
            <div
              className="absolute top-4 right-4 z-30"
              style={{ pointerEvents: "auto" }}
            >
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
                className="w-10 h-10 inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-md backdrop-blur-sm transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Mobile action buttons */}
            <div
              className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30 w-full max-w-md px-4"
              style={{ pointerEvents: "auto" }}
            >
              <div className="flex justify-center gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownload(selectedImage, e);
                  }}
                  className="h-10 px-4 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/10 rounded-md backdrop-blur-md text-[13px] font-medium transition-colors"
                  aria-label="Descargar"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar</span>
                </button>
                {selectedImage && !isVideoItem(selectedImage) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openVideoMenu(selectedImage);
                    }}
                    className="h-10 px-4 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/10 rounded-md backdrop-blur-md text-[13px] font-medium transition-colors"
                    aria-label="Animar"
                  >
                    <Play className="w-4 h-4" />
                    <span>Animar</span>
                  </button>
                )}
                <div ref={moreMenuMobileRef} className="relative">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowMoreMenu(!showMoreMenu);
                    }}
                    className="h-10 px-4 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/10 rounded-md backdrop-blur-md text-[13px] font-medium transition-colors"
                    aria-label="Más opciones"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                    <span>Más</span>
                  </button>
                  {showMoreMenu && (
                    <div
                      className="absolute bottom-full mb-2 right-0 min-w-[200px] bg-[#2C2C2E]/80 backdrop-blur-xl rounded-[14px] py-1.5 px-1.5 animate-fade-in"
                      style={{
                        boxShadow:
                          "0 8px 40px rgba(0,0,0,0.3), 0 0 0 0.5px rgba(255,255,255,0.08)",
                      }}
                    >
                      {!isVideoItem(selectedImage) && (
                        <button
                          onClick={(e) => {
                            handleEditImage(selectedImage, e);
                            setShowMoreMenu(false);
                          }}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-white/85 hover:bg-white/10 transition-colors"
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-white/10 text-white/70">
                            <Pencil className="w-3.5 h-3.5" />
                          </span>
                          Editar
                        </button>
                      )}
                      {!isVideoItem(selectedImage) && (
                        <button
                          onClick={() => {
                            handleShowDescription();
                            setShowMoreMenu(false);
                          }}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-white/85 hover:bg-white/10 transition-colors"
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-white/10 text-white/70">
                            <Instagram className="w-3.5 h-3.5" />
                          </span>
                          Descripción
                        </button>
                      )}
                      {!isVideoItem(selectedImage) && (
                        <button
                          onClick={() => {
                            setShowMoreMenu(false);
                            setShowCropModal(true);
                          }}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-white/85 hover:bg-white/10 transition-colors"
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-white/10 text-white/70">
                            <Crop className="w-3.5 h-3.5" />
                          </span>
                          Recortar
                        </button>
                      )}
                      {!isVideoItem(selectedImage) && (
                        <button
                          onClick={() => {
                            setShowMoreMenu(false);
                            openUpscaleMenu(selectedImage);
                          }}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-white/85 hover:bg-white/10 transition-colors"
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-white/10 text-white/70">
                            <Maximize className="w-3.5 h-3.5" />
                          </span>
                          Mejorar resolución
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          showDeleteConfirmation(selectedImage, e);
                          setShowMoreMenu(false);
                        }}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-red-400 hover:bg-white/10 transition-colors"
                      >
                        <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-red-500/20 text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </span>
                        Eliminar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop layout: absolute positioned like mobile, image fills entire screen */}
          <div
            className={`hidden md:block relative w-full h-full transition-transform duration-200 ease-out ${lightboxOpen ? "scale-100" : "scale-95"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button — top right */}
            <div
              className="absolute top-4 right-4 z-30"
              style={{ pointerEvents: "auto" }}
            >
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeLightbox();
                }}
                className="w-10 h-10 inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-md backdrop-blur-sm transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Right arrow — vertically centered, aligned with right column */}
            <div
              className="absolute inset-y-0 right-4 flex items-center z-20"
              style={{
                pointerEvents: "auto",
                display: showCropModal ? "none" : undefined,
              }}
            >
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (filteredImages.length > 1 && selectedImage) {
                    const currentIndex = filteredImages.findIndex(
                      (img) => img.id === selectedImage.id,
                    );
                    const nextIndex =
                      currentIndex === -1 ||
                      currentIndex + 1 >= filteredImages.length
                        ? 0
                        : currentIndex + 1;
                    const nextImage = filteredImages[nextIndex];
                    if (nextImage) {
                      setSelectedImage(nextImage);
                      setZoomLevel(1);
                      setDragOffset({ x: 0, y: 0 });
                      setShowMoreMenu(false);
                      setMagnifier((m) => ({ ...m, show: false }));
                    }
                  }
                }}
                className="w-10 h-10 inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-md backdrop-blur-sm transition-colors cursor-pointer"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Left arrow */}
            <div
              className="absolute inset-y-0 left-4 flex items-center z-20"
              style={{ display: showCropModal ? "none" : undefined }}
            >
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (filteredImages.length > 1 && selectedImage) {
                    const currentIndex = filteredImages.findIndex(
                      (img) => img.id === selectedImage.id,
                    );
                    const prevIndex =
                      currentIndex <= 0
                        ? filteredImages.length - 1
                        : currentIndex - 1;
                    const prevImage = filteredImages[prevIndex];
                    if (prevImage) {
                      setSelectedImage(prevImage);
                      setZoomLevel(1);
                      setDragOffset({ x: 0, y: 0 });
                      setShowMoreMenu(false);
                      setMagnifier((m) => ({ ...m, show: false }));
                    }
                  }
                }}
                className="w-10 h-10 inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-md backdrop-blur-sm transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            </div>

            {/* Image area — crop canvas or normal image with magnifier */}
            {showCropModal &&
            selectedImage &&
            !isVideoItem(selectedImage) &&
            isDesktopCrop ? (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center"
                style={{ pointerEvents: "auto" }}
              >
                <div
                  className="flex gap-4"
                  style={{ width: "85%", height: "85%" }}
                >
                  <div className="flex-1 relative rounded-lg overflow-hidden">
                    <div className="absolute inset-0">
                      <ImageCropCanvas cropState={cropState} />
                    </div>
                  </div>
                  <div className="w-[280px] flex-shrink-0 bg-surface-card rounded-lg border border-neutral-200 overflow-hidden">
                    <ImageCropControls
                      cropState={cropState}
                      onCancel={() => setShowCropModal(false)}
                      variant="desktop"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="absolute inset-0 overflow-hidden flex items-center justify-center z-10"
                style={{ pointerEvents: "auto" }}
              >
                {expandedImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                {showUpscaleToggle && (
                  <UpscaleComparisonToggle variant="desktop" />
                )}
                {isVideoItem(selectedImage) ? (
                  isDesktopCrop ? (
                    <video
                      key={`desktop-video-${selectedImage.id}`}
                      src={selectedImage.signedUrl}
                      controls
                      playsInline
                      autoPlay
                      className="pointer-events-auto rounded-lg"
                      style={{ maxWidth: "80%", maxHeight: "80%" }}
                      onLoadStart={() => setExpandedImageLoading(true)}
                      onLoadedData={() => setExpandedImageLoading(false)}
                    >
                      Tu navegador no soporta la reproducción de video.
                    </video>
                  ) : null
                ) : (
                  <img
                    ref={desktopImgRef}
                    src={
                      showUpscaleToggle && !showingOriginalAfterUpscale
                        ? selectedImage.upscaledSignedUrl!
                        : selectedImage.signedUrl || selectedImage.thumbnailUrl
                    }
                    alt={`Full screen view of ${selectedImage.file_name}`}
                    className="rounded-lg select-none"
                    style={{
                      // Alto fijo + object-contain: el tamaño mostrado lo fija
                      // el aspect-ratio (igual en `opt` y `full`), evitando el
                      // salto chico→grande al cargar la HD.
                      maxWidth: "85%",
                      height: "85%",
                      width: "auto",
                      objectFit: "contain",
                      opacity: expandedImageLoading ? 0 : 1,
                      cursor: "crosshair",
                    }}
                    onMouseEnter={() => {
                      magnifierTimerRef.current = setTimeout(() => {
                        setMagnifier((m) => ({ ...m, show: true }));
                      }, 300);
                    }}
                    onMouseLeave={() => {
                      if (magnifierTimerRef.current) {
                        clearTimeout(magnifierTimerRef.current);
                        magnifierTimerRef.current = null;
                      }
                      setMagnifier((m) => ({ ...m, show: false }));
                    }}
                    onMouseMove={(e) => {
                      const img = desktopImgRef.current;
                      if (!img) return;
                      const rect = img.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      const percX = (x / rect.width) * 100;
                      const percY = (y / rect.height) * 100;
                      setMagnifier((m) => ({
                        ...m,
                        x: e.clientX,
                        y: e.clientY,
                        bgX: percX,
                        bgY: percY,
                      }));
                    }}
                    onLoadStart={() => setExpandedImageLoading(true)}
                    onLoad={() => setExpandedImageLoading(false)}
                    onError={() => {
                      console.error(
                        `Failed to load expanded image ${selectedImage.id}, attempting to refresh URL`,
                      );
                      setExpandedImageLoading(true);
                      refreshImageUrl(selectedImage);
                    }}
                  />
                )}
              </div>
            )}

            {/* Magnifier lens — shows zoomed crop beside cursor */}
            {magnifier.show &&
              selectedImage &&
              !isVideoItem(selectedImage) &&
              !expandedImageLoading && (
                <div
                  className="fixed z-40 w-80 h-80 rounded-lg border-2 border-white/20 shadow-lg overflow-hidden pointer-events-none transition-opacity duration-200 ease-out"
                  style={{
                    left: magnifier.x + 24,
                    top: magnifier.y - 160,
                    opacity: magnifier.show ? 1 : 0,
                    backgroundImage: `url(${
                      showUpscaleToggle && !showingOriginalAfterUpscale
                        ? selectedImage.upscaledSignedUrl
                        : selectedImage.signedUrl || selectedImage.thumbnailUrl
                    })`,
                    backgroundSize: "400%",
                    backgroundPosition: `${magnifier.bgX}% ${magnifier.bgY}%`,
                    backgroundRepeat: "no-repeat",
                  }}
                />
              )}

            {/* Desktop action buttons — floating container at bottom */}
            <div
              className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30"
              style={{
                pointerEvents: "auto",
                display: showCropModal ? "none" : undefined,
              }}
            >
              <div className="inline-flex items-center gap-2 bg-white/[0.06] backdrop-blur-xl rounded-lg p-2 border border-white/[0.08]">
                {!isVideoItem(selectedImage) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditImage(selectedImage, e);
                    }}
                    className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                  >
                    <Pencil className="w-4 h-4" />
                    <span>Editar</span>
                  </button>
                )}
                {!isVideoItem(selectedImage) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCarruselClick(selectedImage);
                    }}
                    className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Carrusel</span>
                  </button>
                )}
                {!isVideoItem(selectedImage) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openVideoMenu(selectedImage);
                    }}
                    className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                  >
                    <Play className="w-4 h-4" />
                    <span>Animar</span>
                  </button>
                )}
                {!isVideoItem(selectedImage) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShowDescription();
                    }}
                    className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                  >
                    <Instagram className="w-4 h-4" />
                    <span>Descripción</span>
                  </button>
                )}
                {!isVideoItem(selectedImage) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowCropModal(true);
                    }}
                    className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                  >
                    <Crop className="w-4 h-4" />
                    <span>Recortar</span>
                  </button>
                )}
                {!isVideoItem(selectedImage) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openUpscaleMenu(selectedImage);
                    }}
                    className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                  >
                    <Maximize className="w-4 h-4" />
                    <span>Mejorar</span>
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    showDeleteConfirmation(selectedImage, e);
                  }}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Eliminar</span>
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownload(selectedImage, e);
                  }}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] transition-colors cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar</span>
                </button>
              </div>
            </div>
          </div>

          {/* Description panel — modal style */}
          {showDescriptionPanel && selectedImage && (
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[10000]"
              onClick={(e) => {
                e.stopPropagation();
                setShowDescriptionPanel(false);
              }}
            >
              <div
                className="bg-surface-card rounded-lg p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] max-w-md w-full shadow-lg border border-neutral-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[length:var(--text-section-title-mobile)] md:text-[length:var(--text-section-title-desktop)] font-semibold text-neutral-900">
                    {isGeneratingDescription
                      ? "Generando descripción"
                      : "Descripción para post"}
                  </h3>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowDescriptionPanel(false);
                    }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {isGeneratingDescription ? (
                  <div className="mb-6">
                    <div className="flex items-center gap-3 text-neutral-600 mb-4">
                      <Loader2 className="w-5 h-5 animate-spin text-brand-primary shrink-0" />
                      <span className="text-[length:var(--text-body)]">
                        Generando descripción para post de Instagram...
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden bg-neutral-100">
                      <div
                        className="h-full bg-brand-primary rounded-full"
                        style={{
                          width: progressActive ? "100%" : "0%",
                          transition: "width 20s cubic-bezier(0, 0, 0.2, 1)",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-[length:var(--text-body)] text-neutral-600 whitespace-pre-wrap mb-6 max-h-[50vh] overflow-y-auto">
                      {postDescription}
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={() => copyDescription("panel")}
                        className={`h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 ${copiedPanel ? "bg-success hover:bg-success" : "bg-brand-primary hover:bg-brand-primary-hover"} text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors`}
                      >
                        {copiedPanel ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                        {copiedPanel ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Delete Confirmation Dialog — rendered outside lightbox so it works from both desktop and mobile detail view */}
      {showDeleteConfirm && imageToDelete && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[10000]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-surface-card rounded-lg p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] max-w-md w-full shadow-lg border border-neutral-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[length:var(--text-section-title-mobile)] md:text-[length:var(--text-section-title-desktop)] font-semibold text-neutral-900">
                Confirmar eliminación
              </h3>
              <button
                onClick={cancelDelete}
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                disabled={deleting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-[length:var(--text-body)] text-neutral-600">
                ¿Estás seguro de que quieres eliminar esta imagen de{" "}
                <span className="font-semibold text-neutral-900">
                  {imageToDelete.variante_nombre}
                </span>
                ?
              </p>
              <p className="text-[length:var(--text-caption)] text-error mt-1">
                Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteImage}
                className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-error hover:bg-red-700 text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {connectPrompt}
      {showEditPlanRestriction && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[100000]"
          style={{ position: "fixed" }}
        >
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowEditPlanRestriction(false)}
          />
          <div className="relative bg-surface-card rounded-lg w-[95vw] max-w-md p-6 shadow-md">
            <button
              onClick={() => setShowEditPlanRestriction(false)}
              className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-600 transition-colors rounded-sm"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-neutral-900 mb-2">
              Editar con IA disponible en Plan Pro o Ultra
            </h3>
            <p className="text-[length:var(--text-body)] text-neutral-600 mb-5">
              La función de editar con IA está disponible para el plan Pro o
              Ultra. Mejorá tu plan para acceder a esta función y triplicar tu
              límite de fotos mensual.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setShowEditPlanRestriction(false)}
                className="h-[var(--btn-h)] px-5 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                Cancelar
              </button>
              {userPlan === "gratis" || !userPlan || isArgentina ? (
                <button
                  onClick={() => {
                    setShowEditPlanRestriction(false);
                    navigate("/subscription");
                  }}
                  className="h-[var(--btn-h)] px-5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
                >
                  {userPlan === "gratis" || !userPlan ? "Ver planes" : "Mejorar plan"}
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowEditPlanRestriction(false);
                    handleOpenBillingPortal();
                  }}
                  disabled={openingBillingPortal}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
                >
                  {openingBillingPortal ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Gestionar subscripción"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {showCarruselLoading && (
        <LoadingAnimation
          message="Generando carrusel..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={35000}
        />
      )}
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />
      <CarruselConfirmModal
        isOpen={showCarruselConfirm}
        onConfirm={handleGenerateNewAngles}
        onCancel={() => setShowCarruselConfirm(false)}
        imageCount={2}
      />
      {/* Video plan restriction modal */}
      {showVideoPlanRestriction && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-end sm:items-center sm:justify-center sm:p-4"
          style={{ position: "fixed" }}
        >
          <div
            className="fixed inset-0"
            onClick={() => setShowVideoPlanRestriction(false)}
          />
          <div className="relative bg-surface-card w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg shadow-md flex flex-col sm:flex-row sm:overflow-hidden">
            {/* Close button */}
            <button
              onClick={() => setShowVideoPlanRestriction(false)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-sm text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 inline-flex items-center justify-center transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Title — mobile only */}
            <h3 className="sm:hidden text-[17px] font-semibold text-brand-black/90 text-center pt-16 pb-4 px-5">
              Función exclusiva
            </h3>

            {/* Video */}
            <div className="relative flex-shrink-0 px-5 sm:px-0 sm:w-1/2">
              <video
                src="/images/video_animar_imagen.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full max-w-[280px] mx-auto sm:max-w-none aspect-[4/5] object-cover rounded-md sm:rounded-none sm:rounded-l-lg"
              />
              <div className="absolute -bottom-3 right-8 w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center shadow-md sm:hidden">
                <Lock className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col justify-center p-5 pt-8 pb-8 sm:p-6">
              <h3 className="hidden sm:block text-[17px] font-semibold text-brand-black/90 text-center mb-3">
                Función exclusiva
              </h3>
              <p className="text-[15px] text-neutral-500 mb-5 sm:text-center">
                La función de animar imágenes está disponible para los planes{" "}
                <span className="font-semibold text-brand-black/80">Pro</span> y{" "}
                <span className="font-semibold text-brand-black/80">Ultra</span>
                .
              </p>
              <div className="flex flex-col gap-2 mt-auto pb-4 sm:mt-0 sm:pb-0">
                {userPlan === "gratis" || !userPlan || isArgentina ? (
                  <button
                    onClick={() => {
                      setShowVideoPlanRestriction(false);
                      navigate("/subscription");
                    }}
                    className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                  >
                    {userPlan === "gratis" || !userPlan ? "Ver planes" : "Mejorar plan"}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowVideoPlanRestriction(false);
                      handleOpenBillingPortal();
                    }}
                    disabled={openingBillingPortal}
                    className="inline-flex items-center justify-center gap-2 w-full h-10 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                  >
                    {openingBillingPortal ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Gestionar subscripción"
                    )}
                  </button>
                )}
                <button
                  onClick={() => setShowVideoPlanRestriction(false)}
                  className="inline-flex items-center justify-center w-full h-10 bg-surface-card hover:bg-neutral-100 text-neutral-600 border border-neutral-200 rounded-md text-[15px] font-medium shadow-sm transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Video generating loading */}
      {isGeneratingVideo && (
        <LoadingAnimation
          message="Generando video..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={60000}
        />
      )}
      {/* Upscale menu */}
      {showUpscaleMenu &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${upscaleMenuAnimated ? "opacity-100" : "opacity-0"}`}
              onClick={closeUpscaleMenu}
            />
            {/* Desktop: side panel — style matches "Generar video" modal */}
            <div
              className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${upscaleMenuAnimated ? "translate-x-0" : "translate-x-[calc(100%+32px)]"}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4">
                <h3 className="text-[17px] font-semibold text-brand-black/90">
                  Mejorar resolución
                </h3>
                <button
                  type="button"
                  onClick={closeUpscaleMenu}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mx-5 h-px bg-black/[0.08]" />
              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {upscaleTargetImageRef.current && (
                  <div className="flex justify-center mb-6">
                    <img
                      src={upscaleTargetImageRef.current.signedUrl}
                      alt="Preview"
                      className="w-3/5 aspect-[4/5] object-cover rounded-[10px]"
                    />
                  </div>
                )}
                {upscaleTargetImageRef.current?.upscaled_path && (
                  <div className="mb-4 rounded-[13px] bg-warning/10 border border-warning/20 px-4 py-3 flex gap-3">
                    <Info className="w-4 h-4 text-warning-text shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold text-warning-text mb-0.5">
                        Ya tenés una versión mejorada
                      </p>
                      <p className="text-[13px] text-warning-text/80 leading-snug">
                        Al continuar vas a reemplazar la mejora actual y{" "}
                        {upscaleImagesCost === 1
                          ? "se consumirá 1 imagen otra vez."
                          : `se consumirán ${upscaleImagesCost} imágenes otra vez.`}
                      </p>
                    </div>
                  </div>
                )}
                {/* Escala — iOS grouped section */}
                <div>
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                    Escala
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                    <div className="px-4 py-[11px]">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setUpscaleScale(2)}
                          className={`flex-1 h-10 rounded-lg text-[15px] font-medium transition-colors ${upscaleScale === 2 ? "bg-[#1644EB] text-white" : "bg-neutral-100 text-brand-black/70 hover:bg-neutral-200"}`}
                        >
                          2x
                        </button>
                        <button
                          type="button"
                          onClick={() => setUpscaleScale(4)}
                          className={`flex-1 h-10 rounded-lg text-[15px] font-medium transition-colors ${upscaleScale === 4 ? "bg-[#1644EB] text-white" : "bg-neutral-100 text-brand-black/70 hover:bg-neutral-200"}`}
                        >
                          4x
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Información — iOS grouped section */}
                <div className="mt-6">
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                    Información
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                    <div className="px-4 py-[11px]">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                          photo_size_select_large
                        </span>
                        <span className="text-[15px] text-brand-black/90">
                          Resolución {upscaleScale}x
                        </span>
                      </div>
                      <p className="text-[13px] text-[#3C3C43]/60">
                        La imagen se mejorará a {upscaleScale} veces su
                        resolución original usando inteligencia artificial.
                      </p>
                    </div>
                    <div className="ml-[46px] h-px bg-black/[0.08]" />
                    <div className="px-4 py-[11px]">
                      <div className="flex items-center gap-2">
                        <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
                        <span className="text-[13px] text-[#3C3C43]/60">
                          {upscaleImagesCost === 1
                            ? "Se consumirá 1 imagen"
                            : `Se consumirán ${upscaleImagesCost} imágenes`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div className="px-5 py-3 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeUpscaleMenu}
                  className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleUpscale}
                  disabled={isUpscaling}
                  className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors disabled:opacity-50"
                >
                  {isUpscaling ? "Procesando..." : "Mejorar"}
                </button>
              </div>
            </div>
            {/* Mobile: full-height slide-up sheet — style matches "Generar video" modal */}
            <div
              className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
              style={{
                height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                transform:
                  upscaleMenuDragY > 0
                    ? `translateY(${upscaleMenuDragY}px)`
                    : upscaleMenuAnimated
                      ? "translateY(0)"
                      : "translateY(100%)",
                transition:
                  upscaleMenuDragY > 0 ? "none" : "transform 300ms ease-out",
              }}
            >
              <div
                className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing shrink-0"
                onTouchStart={(e) => {
                  upscaleDragStartY.current = e.touches[0].clientY;
                }}
                onTouchMove={(e) => {
                  if (upscaleDragStartY.current !== null) {
                    const dy = e.touches[0].clientY - upscaleDragStartY.current;
                    if (dy > 0) setUpscaleMenuDragY(dy);
                  }
                }}
                onTouchEnd={() => {
                  upscaleDragStartY.current = null;
                  if (upscaleMenuDragY > 100) {
                    closeUpscaleMenu();
                  } else {
                    setUpscaleMenuDragY(0);
                  }
                }}
              >
                <div className="w-9 h-[5px] rounded-full bg-neutral-300" />
              </div>
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 shrink-0">
                <h3 className="text-[15px] font-semibold text-brand-black/90">
                  Mejorar resolución
                </h3>
                <button
                  type="button"
                  onClick={closeUpscaleMenu}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 active:bg-black/[0.1] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {upscaleTargetImageRef.current && (
                  <div className="flex justify-center mb-6">
                    <img
                      src={upscaleTargetImageRef.current.signedUrl}
                      alt="Preview"
                      className="w-3/5 aspect-[4/5] object-cover rounded-[10px]"
                    />
                  </div>
                )}
                {upscaleTargetImageRef.current?.upscaled_path && (
                  <div className="mb-4 rounded-[13px] bg-warning/10 border border-warning/20 px-4 py-3 flex gap-3">
                    <Info className="w-4 h-4 text-warning-text shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold text-warning-text mb-0.5">
                        Ya tenés una versión mejorada
                      </p>
                      <p className="text-[13px] text-warning-text/80 leading-snug">
                        Al continuar vas a reemplazar la mejora actual y{" "}
                        {upscaleImagesCost === 1
                          ? "se consumirá 1 imagen otra vez."
                          : `se consumirán ${upscaleImagesCost} imágenes otra vez.`}
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                    Escala
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                    <div className="px-4 py-[11px]">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setUpscaleScale(2)}
                          className={`flex-1 h-10 rounded-lg text-[15px] font-medium transition-colors ${upscaleScale === 2 ? "bg-[#1644EB] text-white" : "bg-neutral-100 text-brand-black/70 active:bg-neutral-200"}`}
                        >
                          2x
                        </button>
                        <button
                          type="button"
                          onClick={() => setUpscaleScale(4)}
                          className={`flex-1 h-10 rounded-lg text-[15px] font-medium transition-colors ${upscaleScale === 4 ? "bg-[#1644EB] text-white" : "bg-neutral-100 text-brand-black/70 active:bg-neutral-200"}`}
                        >
                          4x
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                    Información
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                    <div className="px-4 py-[11px]">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                          photo_size_select_large
                        </span>
                        <span className="text-[15px] text-brand-black/90">
                          Resolución {upscaleScale}x
                        </span>
                      </div>
                      <p className="text-[13px] text-[#3C3C43]/60">
                        La imagen se mejorará a {upscaleScale} veces su
                        resolución original usando inteligencia artificial.
                      </p>
                    </div>
                    <div className="ml-[46px] h-px bg-black/[0.08]" />
                    <div className="px-4 py-[11px]">
                      <div className="flex items-center gap-2">
                        <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
                        <span className="text-[13px] text-[#3C3C43]/60">
                          {upscaleImagesCost === 1
                            ? "Se consumirá 1 imagen"
                            : `Se consumirán ${upscaleImagesCost} imágenes`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div className="px-5 py-3 flex justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={closeUpscaleMenu}
                  className="text-[15px] text-[#3C3C43]/60 active:text-brand-black/80 px-3 py-2 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleUpscale}
                  disabled={isUpscaling}
                  className="text-[15px] font-semibold text-white bg-brand-primary active:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors disabled:opacity-50"
                >
                  {isUpscaling ? "Procesando..." : "Mejorar"}
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}

      {/* Loading animation for upscaling */}
      {isUpscaling && (
        <LoadingAnimation
          message="Mejorando resolución..."
          subMessage="Esto puede tardar hasta 1 minuto"
          durationMs={60000}
          completeNow={upscaleProgressComplete}
          onCompleted={() => {
            setIsUpscaling(false);
          }}
        />
      )}

      {/* Video generation menu */}
      {showVideoMenu &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${videoMenuAnimated ? "opacity-100" : "opacity-0"}`}
              onClick={closeVideoMenu}
            />
            {/* Desktop: side panel (PruebaNuevaInterfaz style) */}
            <div
              className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${videoMenuAnimated ? "translate-x-0" : "translate-x-[calc(100%+32px)]"}`}
            >
              <div className="flex items-center justify-between px-5 py-4">
                <h3 className="text-[17px] font-semibold text-brand-black/90">
                  {videoStep === "prompt"
                    ? "Configurar video"
                    : "Generar video"}
                </h3>
                <button
                  type="button"
                  onClick={closeVideoMenu}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mx-5 h-px bg-black/[0.08]" />
              <div
                className={`flex-1 overflow-y-auto px-5 py-4${videoStep === "select" ? " flex items-center justify-center" : ""}`}
              >
                {videoStep === "select" ? (
                  <div className="flex flex-col items-center">
                    <video
                      src="/images/video_animar_imagen.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-4/5 mx-auto aspect-[4/5] object-cover rounded-[10px] mb-4"
                    />
                    <p className="text-[15px] text-neutral-400 text-center">
                      Convertí tu imagen en un video animado con movimiento
                      realista generado por IA.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Descripción del video — iOS grouped section */}
                    <div>
                      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                        Descripción del video
                      </p>
                      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                        {loadingVideoPrompt ? (
                          <div className="px-4 py-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Loader2 className="w-5 h-5 text-[#1644EB] animate-spin" />
                              <span className="text-[15px] text-[#3C3C43]/60">
                                Generando idea para el video...
                              </span>
                            </div>
                            <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#1644EB] rounded-full"
                                style={{
                                  animation:
                                    "videoPromptProgress 15s linear forwards",
                                }}
                              />
                            </div>
                            <style>{`@keyframes videoPromptProgress { from { width: 0%; } to { width: 100%; } }`}</style>
                          </div>
                        ) : (
                          <div className="px-4 py-3">
                            <textarea
                              rows={4}
                              value={videoPrompt}
                              onChange={(e) => setVideoPrompt(e.target.value)}
                              placeholder="Ej: Que el modelo camine hacia la cámara con movimiento suave..."
                              className="w-full text-[15px] text-brand-black/90 placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
                            />
                          </div>
                        )}
                      </div>
                      {!loadingVideoPrompt && (
                        <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                          Cuanto más específico seas, mejor será el resultado.
                        </p>
                      )}
                    </div>

                    {/* Configuración — iOS grouped section */}
                    <div className="mt-6">
                      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                        Configuración
                      </p>
                      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                        {/* Duración — slider */}
                        <div className="px-4 py-[11px]">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                              timer
                            </span>
                            <span className="text-[15px] text-brand-black/90">
                              Duración
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-1 flex flex-col">
                              <div className="relative h-[22px] flex items-center">
                                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-neutral-200" />
                                <div
                                  className="absolute top-1/2 -translate-y-1/2 left-0 h-[6px] rounded-full bg-[#1644EB]"
                                  style={{
                                    width: `${((videoDuration - 4) / 11) * 100}%`,
                                  }}
                                />
                                <input
                                  type="range"
                                  min={4}
                                  max={15}
                                  step={1}
                                  value={videoDuration}
                                  onChange={(e) =>
                                    setVideoDuration(Number(e.target.value))
                                  }
                                  className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[22px] [&::-webkit-slider-thumb]:h-[22px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[22px]"
                                />
                              </div>
                              <div className="flex justify-between px-[2px] mt-1">
                                {Array.from({ length: 12 }, (_, i) => (
                                  <div
                                    key={i}
                                    className={`w-[3px] h-[3px] rounded-full ${i + 4 <= videoDuration ? "bg-[#1644EB]/40" : "bg-neutral-300"}`}
                                  />
                                ))}
                              </div>
                            </div>
                            <span className="text-[15px] font-medium text-brand-black/90 tabular-nums shrink-0 min-w-[58px] text-right">
                              {videoDuration} seg
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
                            <span className="text-[13px] text-[#3C3C43]/60">
                              Se consumirán {videoImagesCost} imágenes
                            </span>
                          </div>
                        </div>

                        <div className="ml-[46px] h-px bg-black/[0.08]" />

                        {/* Múltiples escenas — toggle */}
                        <div className="px-4 py-[11px]">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                                theaters
                              </span>
                              <span className="text-[15px] text-brand-black/90">
                                Múltiples escenas
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setVideoMultiScene(!videoMultiScene)
                              }
                              className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${videoMultiScene ? "bg-[#1644EB]" : "bg-neutral-300"}`}
                            >
                              <div
                                className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] transition-transform duration-200 ${videoMultiScene ? "translate-x-[20px]" : "translate-x-0"}`}
                              />
                            </button>
                          </div>
                          {videoMultiScene && (
                            <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                              El video se dividirá en varias escenas con
                              transiciones automáticas para crear un resultado
                              más dinámico y cinematográfico.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="px-5 py-3 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={
                    videoStep === "prompt"
                      ? () => setVideoStep("select")
                      : closeVideoMenu
                  }
                  className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                >
                  {videoStep === "prompt" ? "Volver" : "Cancelar"}
                </button>
                {videoStep === "select" ? (
                  <button
                    type="button"
                    onClick={handleGoToPromptStep}
                    className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleGenerateVideo}
                    className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
                  >
                    Generar video
                  </button>
                )}
              </div>
            </div>
            {/* Mobile: full-height slide-up sheet (PruebaNuevaInterfaz style) */}
            <div
              className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
              style={{
                height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                transform:
                  videoMenuDragY > 0
                    ? `translateY(${videoMenuDragY}px)`
                    : videoMenuAnimated
                      ? "translateY(0)"
                      : "translateY(100%)",
                transition:
                  videoMenuDragY === 0 ? "transform 0.3s ease-out" : "none",
              }}
            >
              {/* Drag handle */}
              <div
                className="py-2 cursor-grab active:cursor-grabbing"
                onTouchStart={(e) => {
                  videoDragStartY.current = e.touches[0].clientY;
                }}
                onTouchMove={(e) => {
                  if (videoDragStartY.current !== null) {
                    const dy = e.touches[0].clientY - videoDragStartY.current;
                    if (dy > 0) setVideoMenuDragY(dy);
                  }
                }}
                onTouchEnd={() => {
                  if (videoMenuDragY > 100) {
                    closeVideoMenu();
                  } else {
                    setVideoMenuDragY(0);
                  }
                  videoDragStartY.current = null;
                }}
              >
                <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
              </div>
              {/* Header: Cancelar | Title | Action */}
              <div className="flex items-center justify-between px-4 pb-3">
                <button
                  type="button"
                  onClick={
                    videoStep === "prompt"
                      ? () => setVideoStep("select")
                      : closeVideoMenu
                  }
                  className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                >
                  {videoStep === "prompt" ? "Volver" : "Cancelar"}
                </button>
                <h3 className="text-[15px] font-semibold text-brand-black/90">
                  {videoStep === "prompt"
                    ? "Configurar video"
                    : "Generar video"}
                </h3>
                {videoStep === "select" ? (
                  <button
                    type="button"
                    onClick={handleGoToPromptStep}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleGenerateVideo}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Generar
                  </button>
                )}
              </div>
              {/* Body */}
              <div
                className={`flex-1 overflow-y-auto px-4 py-4${videoStep === "select" ? " flex items-center justify-center" : " bg-surface-bg"}`}
              >
                {videoStep === "select" ? (
                  <div className="flex flex-col items-center">
                    <video
                      src="/images/video_animar_imagen.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-4/5 mx-auto aspect-[4/5] object-cover rounded-[10px] mb-4"
                    />
                    <p className="text-[15px] text-neutral-400 text-center">
                      Convertí tu imagen en un video animado con movimiento
                      realista generado por IA.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Descripción del video — iOS grouped section */}
                    <div>
                      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                        Descripción del video
                      </p>
                      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                        {loadingVideoPrompt ? (
                          <div className="px-4 py-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Loader2 className="w-5 h-5 text-[#1644EB] animate-spin" />
                              <span className="text-[15px] text-[#3C3C43]/60">
                                Generando idea para el video...
                              </span>
                            </div>
                            <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#1644EB] rounded-full"
                                style={{
                                  animation:
                                    "videoPromptProgress 15s linear forwards",
                                }}
                              />
                            </div>
                            <style>{`@keyframes videoPromptProgress { from { width: 0%; } to { width: 100%; } }`}</style>
                          </div>
                        ) : (
                          <div className="px-4 py-3">
                            <textarea
                              rows={4}
                              value={videoPrompt}
                              onChange={(e) => setVideoPrompt(e.target.value)}
                              placeholder="Ej: Que el modelo camine hacia la cámara con movimiento suave..."
                              className="w-full text-[15px] text-brand-black/90 placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
                            />
                          </div>
                        )}
                      </div>
                      {!loadingVideoPrompt && (
                        <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                          Cuanto más específico seas, mejor será el resultado.
                        </p>
                      )}
                    </div>

                    {/* Configuración — iOS grouped section */}
                    <div className="mt-6">
                      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                        Configuración
                      </p>
                      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                        {/* Duración — slider */}
                        <div className="px-4 py-[11px]">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                              timer
                            </span>
                            <span className="text-[15px] text-brand-black/90">
                              Duración
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-1 flex flex-col">
                              <div className="relative h-[22px] flex items-center">
                                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-neutral-200" />
                                <div
                                  className="absolute top-1/2 -translate-y-1/2 left-0 h-[6px] rounded-full bg-[#1644EB]"
                                  style={{
                                    width: `${((videoDuration - 4) / 11) * 100}%`,
                                  }}
                                />
                                <input
                                  type="range"
                                  min={4}
                                  max={15}
                                  step={1}
                                  value={videoDuration}
                                  onChange={(e) =>
                                    setVideoDuration(Number(e.target.value))
                                  }
                                  className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[22px] [&::-webkit-slider-thumb]:h-[22px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[22px]"
                                />
                              </div>
                              <div className="flex justify-between px-[2px] mt-1">
                                {Array.from({ length: 12 }, (_, i) => (
                                  <div
                                    key={i}
                                    className={`w-[3px] h-[3px] rounded-full ${i + 4 <= videoDuration ? "bg-[#1644EB]/40" : "bg-neutral-300"}`}
                                  />
                                ))}
                              </div>
                            </div>
                            <span className="text-[15px] font-medium text-brand-black/90 tabular-nums shrink-0 min-w-[58px] text-right">
                              {videoDuration} seg
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
                            <span className="text-[13px] text-[#3C3C43]/60">
                              Se consumirán {videoImagesCost} imágenes
                            </span>
                          </div>
                        </div>

                        <div className="ml-[46px] h-px bg-black/[0.08]" />

                        {/* Múltiples escenas — toggle */}
                        <div className="px-4 py-[11px]">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                                theaters
                              </span>
                              <span className="text-[15px] text-brand-black/90">
                                Múltiples escenas
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setVideoMultiScene(!videoMultiScene)
                              }
                              className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${videoMultiScene ? "bg-[#1644EB]" : "bg-neutral-300"}`}
                            >
                              <div
                                className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] transition-transform duration-200 ${videoMultiScene ? "translate-x-[20px]" : "translate-x-0"}`}
                              />
                            </button>
                          </div>
                          {videoMultiScene && (
                            <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                              El video se dividirá en varias escenas con
                              transiciones automáticas para crear un resultado
                              más dinámico y cinematográfico.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}

      {showInAppBrowserModal && (
        <InAppBrowserModal onClose={() => setShowInAppBrowserModal(false)} />
      )}
    </div>
  );
};
